/* ============================================================
   BLUE SALON — PRODUCT DETAIL PAGE (PDP)
   Drives the server-rendered markup of the product template's three
   sections: gallery zoom/lightbox, colour/size selection (via the
   variants JSON embedded in the page), sticky buy bar, accordion,
   share, wishlist, Ajax add-to-cart and the Mozoon loyalty drawer in
   sections/product.liquid, plus the session-only reviews demo in
   sections/pdp-reviews.liquid and the recommendations fetch behind
   sections/pdp-related.liquid. Loaded once for the whole template, not
   per section (see layout/theme.liquid), so #pdp — the main section —
   is what it keys off; the other two are optional and simply absent
   when the merchant removes them.
   ============================================================ */
(function () {
  "use strict";

  var root = document.getElementById("pdp");
  if (!root) return;

  function storefrontUrl(path) {
    if (window.BS && typeof window.BS.storefrontUrl === "function") return window.BS.storefrontUrl(path);
    var shopRoot = window.Shopify && window.Shopify.routes && window.Shopify.routes.root;
    return (shopRoot || "/") + String(path || "").replace(/^\//, "");
  }

  /* ── Small helpers ────────────────────────────────────────── */
  function byId(id) { return document.getElementById(id); }

  function escapeHtml(str) {
    return String(str == null ? "" : str)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");
  }

  function parseJSON(id) {
    var el = byId(id);
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }

  // navigator.clipboard needs a secure context (HTTPS/localhost) — falls
  // back to the older execCommand approach (a temporary offscreen textarea)
  // so "copy link" still works over plain HTTP or on older browsers instead
  // of silently doing nothing.
  //
  // `fail` runs when both routes refuse — a browser that blocks clipboard
  // access, or a call that did not come from a real tap or click. Without it
  // the caller only ever heard about success, so a failed copy closed the
  // share menu and said nothing, which reads exactly like a copy that worked.
  function copyText(text, done, fail) {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(done).catch(function () { legacyCopy(text, done, fail); });
    } else {
      legacyCopy(text, done, fail);
    }
  }
  function legacyCopy(text, done, fail) {
    var ta = document.createElement("textarea");
    ta.value = text;
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    var copied = false;
    try {
      copied = document.execCommand("copy");
    } catch (e) {}
    document.body.removeChild(ta);
    if (copied) done();
    else if (fail) fail();
  }

  // Copy this file builds at runtime, published by
  // snippets/storefront-i18n.liquid. English literals stay as fallbacks so
  // the page still reads correctly if the bridge has not loaded.
  var T = (window.BS_I18N || {}).product || {};

  // Arabic does not build "only N left." from fragments the way English
  // does, so the whole sentence is one key with the count substituted in.
  function lowStockText(qty) {
    if (qty === 1) return T.low_stock_one || "Low in stock: only 1 left.";
    return (T.low_stock_many || "Low in stock: only __COUNT__ left.")
      .replace("__COUNT__", qty);
  }

  function selectOptionText(label) {
    return (T.please_select || "Please select a __NAME__").replace("__NAME__", label) + ".";
  }

  var ICON_CHECK = '<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5"/></svg>';

  /* ── Mobile toast: brief "Added to your bag"/wishlist confirmation ──
     Mobile only (see isMobileViewport) — opening the full cart drawer for
     every add-to-bag/wishlist tap is a lot on a small screen, so mobile
     gets this instead; desktop keeps the drawer/no notification. */
  function isMobileViewport() {
    return window.matchMedia && window.matchMedia("(max-width: 900px)").matches;
  }
  var toastEl = byId("pdpToast");
  var toastTextEl = byId("pdpToastText");
  var toastIconEl = byId("pdpToastIcon");
  var toastTimer = null;
  // isError swaps the pill for a full-width red banner (.is-error in
  // product.css). Unlike the confirmations, errors are shown on desktop
  // too — callers there get no cart drawer to explain a failed add. icon is
  // an optional inline SVG for the confirmation pill (a gold heart for the
  // wishlist), mirroring the PLP's showToast (assets/listing.js).
  function showToast(message, isError, icon) {
    if (!toastEl || !toastTextEl) return;
    toastTextEl.textContent = message;
    if (toastIconEl) toastIconEl.innerHTML = isError ? "" : (icon || "");
    toastEl.classList.toggle("is-error", !!isError);
    toastEl.setAttribute("role", isError ? "alert" : "status");
    toastEl.classList.add("is-visible");

    // Desktop renders the error in flow under Add to bag, which is well
    // off-screen when the tap came from the sticky bar (that bar only
    // exists once the buy row has scrolled away). The mobile band is
    // position:fixed and always on screen, so it's skipped here.
    if (isError && getComputedStyle(toastEl).position === "static") {
      var box = toastEl.getBoundingClientRect();
      if (box.top < 0 || box.bottom > window.innerHeight) {
        toastEl.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }

    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () {
      toastEl.classList.remove("is-visible");
    }, isError ? 5000 : 2500);
  }

  // Champagne-gold heart glyphs for the wishlist confirmation pill — filled
  // on add, outline on remove (same as the PLP's).
  var WISH_ICON_ADDED =
    '<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" ' +
    'stroke="currentColor" stroke-width="1.6" stroke-linejoin="round">' +
    '<path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.6l-1-1a5.5 5.5 0 1 0-7.8 7.8l1 1L12 21l7.8-7.6 1-1a5.5 5.5 0 0 0 0-7.8z"/></svg>';
  var WISH_ICON_REMOVED =
    '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" ' +
    'stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">' +
    '<path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.6l-1-1a5.5 5.5 0 1 0-7.8 7.8l1 1L12 21l7.8-7.6 1-1a5.5 5.5 0 0 0 0-7.8z"/></svg>';

  /* ── Mobile "added to your bag" card (matches the PLP's) ──────────
     Shown on mobile in place of the cart drawer preview: fills in the added
     product's image/title/variant from the /cart/add.js line item, runs the
     brand-blue countdown bar, then auto-dismisses. `item` is data.items[0];
     falls back to the drawer preview if the markup or item is missing. */
  var cartToastTimer = null;
  var CART_TOAST_MS = 2000;

  function showCartToast(item) {
    var toast = byId("pdpCartToast");
    if (!toast || !item) {
      openCartDrawer();
      return;
    }
    var img = byId("pdpCartToastImg");
    var title = byId("pdpCartToastTitle");
    var meta = byId("pdpCartToastMeta");
    var bar = byId("pdpCartToastBar");
    var src = (item.featured_image && item.featured_image.url) || item.image || "";
    if (img) {
      img.removeAttribute("srcset");
      img.src = src;
      img.alt = item.product_title || item.title || "";
    }
    if (title) title.textContent = item.product_title || item.title || "";
    if (meta) {
      // "Default Title" is Shopify's placeholder for a variant-less product.
      var variant = item.variant_title && item.variant_title !== "Default Title"
        ? item.variant_title : "";
      meta.textContent = variant;
      meta.hidden = !variant;
    }
    toast.setAttribute("aria-hidden", "false");
    toast.classList.add("is-visible");
    if (bar) {
      bar.style.transition = "none";
      bar.style.transform = "scaleX(1)";
      void bar.offsetWidth;
      bar.style.transition = "transform " + CART_TOAST_MS + "ms linear";
      bar.style.transform = "scaleX(0)";
    }
    clearTimeout(cartToastTimer);
    cartToastTimer = setTimeout(function () {
      toast.classList.remove("is-visible");
      toast.setAttribute("aria-hidden", "true");
    }, CART_TOAST_MS);
  }

  /* ── Variants: drives colour/size selection, live price/specs ── */
  var variants = parseJSON("pdpVariantsJson") || [];

  /* Options are addressed by POSITION (Shopify's option1/2/3), never by
     name. A product here may carry Colour, Size, Width, Volume or
     anything a merchant adds later, in any order — position is the only
     stable key, and matching on all of them is what stops a shopper
     being silently resolved onto, say, the wrong shoe width.

     optionPositions holds just the positions that rendered a picker;
     single-value options are already decided and need no selection. */
  var optionPositions = [];
  root.querySelectorAll("[data-opt-pos]").forEach(function (el) {
    var pos = parseInt(el.dataset.optPos, 10);
    if (pos && optionPositions.indexOf(pos) === -1) optionPositions.push(pos);
  });
  optionPositions.sort(function (a, b) { return a - b; });

  var optionNames = {};
  root.querySelectorAll("[data-opt-block]").forEach(function (el) {
    optionNames[el.dataset.optBlock] = el.dataset.optName || "option";
  });

  // The shared resolver in assets/bs-variants.js — the same one the product
  // card, the Quick View drawer and the wishlist drawer call, so a partial
  // pick resolves identically on every surface instead of four copies of
  // the rule agreeing only for as long as nobody edits one of them.
  function findVariant(selected) {
    return window.BSVariants.resolve(variants, selected, optionPositions);
  }

  // Liquid bakes each swatch/chip's sold-out state once at page load, relative
  // to whichever variant is initially selected (product.options_with_values
  // value.available is context-sensitive, not a flat per-value flag). Picking
  // a different option afterwards never re-ran that Liquid, so e.g. a size
  // genuinely in stock for the newly-picked colour kept showing as sold out
  // (still reflecting the *previous* colour's availability), and vice versa.
  // Re-derive every button's availability client-side, from the actual
  // variants array filtered on every OTHER currently-selected position.
  // Which option is colour. The Liquid already decided (is_color in
  // sections/product.liquid) and renders that block's buttons inside
  // .pdp-swatches rather than .pdp-sizes, so that is read back here instead
  // of re-deriving it from the option name.
  var colorPositions = {};
  root.querySelectorAll("[data-opt-block]").forEach(function (block) {
    if (block.querySelector(".pdp-swatches")) colorPositions[block.dataset.optBlock] = true;
  });

  function recomputeOptionAvailability() {
    optionPositions.forEach(function (pos) {
      root.querySelectorAll('[data-opt-pos="' + pos + '"]').forEach(function (btn) {
        var value = btn.dataset.optValue;
        // Colour is judged across every size, so a colour is only drawn
        // sold out when nothing in it is in stock — see optionAvailable in
        // assets/bs-variants.js. Sizes are still judged within the colour.
        var available = window.BSVariants.optionAvailable(
          variants, optionPositions, state.selected, pos, value, !!colorPositions[pos]
        );
        btn.classList.toggle("is-soldout", !available);
        // Not disabled: picking a sold-out value is how the shopper gets to
        // the back-in-stock block under the picker (see the @app outlet in
        // sections/product.liquid). applyVariant flips the buy buttons to
        // "Sold Out" for it, which is the whole feedback that's needed.
        // data-opt-value is the bare name, so it doubles as the un-suffixed
        // label; only swatches carry a title (their face is a photo, not text).
        var label = btn.dataset.optValue +
          (available ? "" : " " + (T.sold_out_paren || "(Sold out)"));
        btn.setAttribute("aria-label", label);
        if (btn.hasAttribute("title")) btn.setAttribute("title", label);
      });
    });
  }

  // Seeded from whatever the server marked active, so the initial state
  // matches the rendered variant without duplicating the Liquid's choice.
  var state = { selected: {} };
  optionPositions.forEach(function (pos) {
    var active = root.querySelector('[data-opt-pos="' + pos + '"].is-active');
    if (active) state.selected[pos] = active.dataset.optValue;
  });

  // Liquid's initial bake can be wrong the same way it's wrong after a click
  // (see recomputeOptionAvailability's comment above) — run it once up front
  // so a value that's sold out for the pre-selected option is already shown
  // that way, instead of only updating after the shopper picks something else.
  recomputeOptionAvailability();

  // Backfill a photo thumbnail for any colour swatch Liquid couldn't print one
  // for (no variant image and no Shopify swatch.image), falling back to the
  // main product photo so it never renders as a blank chip. Swatches that
  // already carry .pdp-swatch-img were baked by product-option-values.liquid
  // and are left alone, so there is no flat-chip → photo flip on load.
  (function renderSwatchThumbnails() {
    var mainImgEl = byId("pdpMainImg");
    var mainImgFallback = mainImgEl ? mainImgEl.src : null;
    root.querySelectorAll(".pdp-swatch:not(.pdp-swatch-img)").forEach(function (btn) {
      var url = btn.dataset.thumb || btn.dataset.image || mainImgFallback;
      if (!url) return;
      btn.classList.add("pdp-swatch-img");
      btn.style.backgroundImage = "url('" + url + "')";
      btn.style.backgroundColor = "";
    });
  })();

  /* ── Wishlist (localStorage; same "bs_wishlist" convention as assets/listing.js) ── */
  function getWishlist() {
    try { return JSON.parse(localStorage.getItem("bs_wishlist") || "[]"); } catch (e) { return []; }
  }
  function setWishlist(ids) {
    try { localStorage.setItem("bs_wishlist", JSON.stringify(ids)); } catch (e) {}
  }
  function updateWishCount(count) {
    document.querySelectorAll("#wishCount").forEach(function (el) {
      el.textContent = count;
      el.hidden = count === 0;
    });
  }
  // "bs_wishlist_meta": id -> {handle} — see assets/listing.js's
  // saveWishlistHandle for why (lets the wishlist page fetch this product
  // directly by handle instead of scanning the whole /products.json feed).
  function saveWishlistHandle(id, handle) {
    if (!handle) return;
    try {
      var meta = JSON.parse(localStorage.getItem("bs_wishlist_meta") || "{}");
      meta[id] = { handle: handle };
      localStorage.setItem("bs_wishlist_meta", JSON.stringify(meta));
    } catch (e) {}
  }
  // Same fallback as assets/listing.js's extractProductHandle: not every
  // [data-wish-btn] carries data-product-handle (the USF search app's own
  // grid template doesn't), so recover it from the nearest product-link <a>
  // instead, or from the current URL when the button lives on the PDP itself.
  function extractProductHandle(btn) {
    if (btn.dataset.productHandle) return btn.dataset.productHandle;
    var media = btn.closest("[data-card-media]");
    var href = media && media.tagName === "A" ? media.getAttribute("href") : null;
    if (!href) href = window.location.pathname;
    var match = href && href.match(/\/products\/([^/?#]+)/);
    return match ? match[1] : null;
  }
  function toggleWishlist(btn) {
    var id = btn.dataset.productId;
    if (!id) return;
    var ids = getWishlist();
    var idx = ids.indexOf(id);
    var active;
    if (idx === -1) { ids.push(id); active = true; } else { ids.splice(idx, 1); active = false; }
    setWishlist(ids);
    if (active) saveWishlistHandle(id, extractProductHandle(btn));
    // Toggle every button for this product (main image + any related card).
    document.querySelectorAll('[data-wish-btn][data-product-id="' + id + '"]').forEach(function (el) {
      el.classList.toggle("is-active", active);
      el.setAttribute("aria-pressed", active ? "true" : "false");
    });
    updateWishCount(ids.length);
    showToast(
      active
        ? (T.added_wishlist || "Added to your wishlist")
        : (T.removed_wishlist || "Removed from your wishlist"),
      false,
      active ? WISH_ICON_ADDED : WISH_ICON_REMOVED
    );
  }
  function hydrateWishlist() {
    var ids = getWishlist();
    document.querySelectorAll("[data-wish-btn]").forEach(function (btn) {
      var active = ids.indexOf(btn.dataset.productId) !== -1;
      btn.classList.toggle("is-active", active);
      btn.setAttribute("aria-pressed", active ? "true" : "false");
    });
    updateWishCount(ids.length);
  }

  /* ── Cart: Ajax add + count refresh ───────────────────────── */
  // Reuses the site-wide cart drawer already wired up in assets/blue-salon.js
  // (initCartDrawer) — clicking its trigger opens it AND makes it re-fetch
  // /cart.js and re-render, so it always reflects whatever was just added
  // without duplicating that drawer's render logic here. BS_showCartPreview
  // opens it as a brief auto-dismissing confirmation (a few seconds, pauses
  // on hover) instead of staying open like a deliberate header-icon click.
  function openCartDrawer() {
    if (window.BS_showCartPreview) {
      window.BS_showCartPreview();
      return;
    }
    var btn = document.getElementById("cartOpenBtn");
    if (btn) btn.click();
  }

  function addToCart(variantId) {
    var addUrl = root.dataset.cartAddUrl || storefrontUrl("cart/add.js");
    return fetch(addUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
    })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        if (data.status) {
          console.error("Add to cart failed:", data.message || data.description);
          // 422 is Shopify refusing on stock: the shopper already holds
          // every unit there is (the common case on a "only 1 left"
          // variant they've already added). Without this the button just
          // quietly does nothing and the add looks broken.
          showToast(
            data.status === 422
              ? (T.already_max ||
                 "You've already added this to your bag — that's all we have in stock.")
              : (T.add_failed ||
                 "Sorry, we couldn't add that to your bag. Please try again."),
            true
          );
          return false;
        }
        // Mobile gets the light auto-dismissing "added to bag" card instead
        // of the full drawer — a lot to open for every tap on a small screen
        // (same split as the PLP). Desktop opens the drawer preview. Either
        // path already re-fetches /cart.js and syncs the header count itself
        // (see render()'s syncHeaderCount in blue-salon.js) — updateCartCount()
        // below only runs as a fallback so a plain add never fires that same
        // /cart.js request twice over.
        var added = data.items && data.items[0];
        var headerWillSync = false;
        if (isMobileViewport()) {
          showCartToast(added);
          // Keep the (unopened) bag drawer in sync so it's instant + correct
          // when the shopper opens it later — the card path never opens it.
          if (window.BS_refreshCart) { window.BS_refreshCart(); headerWillSync = true; }
          // Brings the auto-hidden mobile topbar back so the bag count is visible.
          document.dispatchEvent(new CustomEvent("bs:cart:added"));
        } else {
          headerWillSync = !!window.BS_showCartPreview;
          openCartDrawer();
        }
        if (!headerWillSync) updateCartCount();
        return true;
      })
      .catch(function (err) {
        console.error(err);
        showToast(T.add_failed ||
          "Sorry, we couldn't add that to your bag. Please try again.", true);
        return false;
      });
  }

  function updateCartCount() {
    fetch(storefrontUrl("cart.js"), { headers: { Accept: "application/json" } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        document.querySelectorAll("#cartCount").forEach(function (el) {
          el.textContent = cart.item_count;
          el.hidden = cart.item_count === 0;
        });
      })
      .catch(function () {});
  }

  // textEl lets a button keep a non-text child (the "+" icon on the Add to
  // bag buttons) that a plain btn.textContent write would otherwise wipe
  // out — defaults to the button itself for plain-text buttons like the
  // "Complete the Look" quick-shop one.
  function flashButton(btn, doneText, resetText, ms, textEl) {
    if (!btn) return;
    var target = textEl || btn;
    btn.classList.add("is-added");
    target.textContent = doneText;
    setTimeout(function () {
      btn.classList.remove("is-added");
      target.textContent = resetText;
    }, ms || 1600);
  }

  /* ── Gallery ──────────────────────────────────────────────── */
  var mainImg = byId("pdpMainImg");
  var sbThumb = byId("pdpSbThumb");
  var galleryEl = byId("pdpGallery");
  var thumbsWrap = byId("pdpThumbs");
  var galleryThumbs = Array.prototype.slice.call(root.querySelectorAll("#pdpThumbs [data-thumb]"));
  var gallerySrcs = galleryThumbs.map(function (t) { return t.dataset.src; });
  if (!gallerySrcs.length && mainImg) gallerySrcs = [mainImg.src];

  // Tracks whichever src setPhoto() was last given, in the exact string
  // form stored in gallerySrcs/data-src (often protocol-relative). Looking
  // this up via mainImg.src instead doesn't work — the <img> src property
  // always reads back as a fully-resolved absolute URL, which never
  // string-matches a protocol-relative gallerySrcs entry, so indexOf(...)
  // against it silently always returns -1.
  var currentPhotoSrc = null;

  // The full, unfiltered gallery (every product image) — the last-resort
  // fallback for a variant with neither its own photo range nor a colour
  // gallery (see applyVariantGallery below). The initially rendered
  // thumbnails are already filtered, so they can't be used as this fallback.
  var defaultGalleryItems = parseJSON("pdpFullGalleryJson") || [];

  // Mobile peek carousel (see the ≤900px full-bleed rules in product.css)
  // — a real horizontal scroll-snap track, kept in sync with the desktop
  // thumb rail/main image rather than driving them independently.
  var mobileTrack = byId("pdpMobileTrack");
  var mobileProgressFill = byId("pdpMobileProgressFill");
  var mobileSlides = mobileTrack ? Array.prototype.slice.call(mobileTrack.querySelectorAll("[data-mobile-slide]")) : [];
  // Set only while setPhoto() is programmatically scrolling the track
  // (thumb click, swatch/size change) — the scrollend handler below skips
  // syncing back in that window so it doesn't fight/re-trigger itself.
  var mobileScrollIsProgrammatic = false;

  function updateMobileProgress() {
    if (!mobileTrack || !mobileProgressFill) return;
    var max = mobileTrack.scrollWidth - mobileTrack.clientWidth;
    var fraction = max > 0 ? mobileTrack.scrollLeft / max : 0;
    // Floored at 10% even on the very first photo — at a true 0% the fill
    // is invisible against its own track, so there's nothing to signal
    // "this is a draggable progress bar" until the shopper already swipes.
    mobileProgressFill.style.width = Math.max(10, Math.min(1, fraction) * 100) + "%";
  }

  function nearestMobileSlideIndex() {
    var scrollLeft = mobileTrack.scrollLeft;
    var closest = 0, closestDist = Infinity;
    mobileSlides.forEach(function (s, i) {
      var dist = Math.abs(s.offsetLeft - scrollLeft);
      if (dist < closestDist) { closestDist = dist; closest = i; }
    });
    return closest;
  }

  // Parallax fade as the page scrolls the mobile gallery out of view: it
  // lags behind the normal scroll (translateY counteracts part of it) and
  // fades out, instead of just snapping away like the rest of the page —
  // a bit of visual interest scrolling into the details below.
  //
  // Driven by a continuous requestAnimationFrame loop, NOT a "scroll"
  // event listener — iOS Safari throttles/coalesces (sometimes skips
  // entirely) "scroll" event dispatch during a finger-driven momentum
  // scroll, so an event-driven version visibly stutters or does nothing
  // while actually scrolling with a thumb, even though it works fine with
  // a mouse wheel or a DevTools/trackpad-driven "scroll". Reading
  // getBoundingClientRect() every animation frame instead sidesteps that
  // entirely. An IntersectionObserver starts/stops the loop so it isn't
  // running (burning battery) while the gallery is off-screen or on
  // desktop, where .pdp-mobile-gallery is display:none and never
  // intersects.
  var mobileGallery = byId("pdpMobileGallery");
  if (mobileGallery && "IntersectionObserver" in window) {
    var parallaxRafId = null;
    function updateMobileParallax() {
      var rect = mobileGallery.getBoundingClientRect();
      var scrolledPast = Math.max(0, -rect.top);
      if (scrolledPast === 0) {
        mobileGallery.style.transform = "";
        mobileGallery.style.opacity = "";
      } else {
        var fadeDistance = rect.height * 0.7;
        var opacity = Math.max(0, 1 - scrolledPast / fadeDistance);
        var lag = Math.min(scrolledPast * 0.3, rect.height * 0.3);
        mobileGallery.style.transform = "translateY(" + lag + "px)";
        mobileGallery.style.opacity = opacity;
      }
      parallaxRafId = requestAnimationFrame(updateMobileParallax);
    }
    new IntersectionObserver(function (entries) {
      var near = entries[0].isIntersecting;
      if (near && parallaxRafId === null) {
        parallaxRafId = requestAnimationFrame(updateMobileParallax);
      } else if (!near && parallaxRafId !== null) {
        cancelAnimationFrame(parallaxRafId);
        parallaxRafId = null;
        mobileGallery.style.transform = "";
        mobileGallery.style.opacity = "";
      }
      // A generous rootMargin starts/stops the loop a full viewport height
      // early/late so the effect is already running by the time the
      // gallery reaches the point where it visibly needs to react.
    }, { rootMargin: "100% 0px 100% 0px" }).observe(mobileGallery);
  }

  if (mobileTrack) {
    updateMobileProgress();
    mobileTrack.addEventListener("scroll", updateMobileProgress, { passive: true });

    var mobileScrollEndTimer = null;
    function onMobileTrackSettled() {
      if (mobileScrollIsProgrammatic || !mobileSlides.length) return;
      var slide = mobileSlides[nearestMobileSlideIndex()];
      if (slide) setPhoto(slide.dataset.src);
    }
    if ("onscrollend" in window) {
      mobileTrack.addEventListener("scrollend", onMobileTrackSettled, { passive: true });
    } else {
      // Safari < 16.4 has no scrollend — treat "no scroll event for
      // 120ms" as settled instead.
      mobileTrack.addEventListener("scroll", function () {
        clearTimeout(mobileScrollEndTimer);
        mobileScrollEndTimer = setTimeout(onMobileTrackSettled, 120);
      }, { passive: true });
    }
  }

  // Scrolls the rail to reveal the active thumb plus a peek of whatever
  // comes right after it — a plain scrollIntoView("nearest") leaves the
  // rail untouched once the active thumb is already fully on-screen, which
  // reads as "the rail is stuck" when there are more thumbs still hidden
  // just below it.
  function scrollThumbIntoView(thumb) {
    if (!thumbsWrap || !thumb) return;
    var next = thumb.nextElementSibling;
    var peek = next ? next.offsetHeight * 0.5 : 0;
    var top = thumb.offsetTop;
    var bottom = top + thumb.offsetHeight + peek;
    if (bottom > thumbsWrap.scrollTop + thumbsWrap.clientHeight) {
      thumbsWrap.scrollTop = bottom - thumbsWrap.clientHeight;
    } else if (top < thumbsWrap.scrollTop) {
      thumbsWrap.scrollTop = top;
    }
  }

  // Crossfade the desktop main image on a thumbnail / variant change instead
  // of a hard src swap: fade the current photo out, and only swap the src once
  // the new photo has BOTH preloaded and the fade finished — then fade it back
  // in. Guards against a blank flash and against rapid clicks racing (token).
  // A same-src set (initial render, re-clicking the active thumb) is instant.
  var mainSwapToken = 0;
  var MAIN_FADE_MS = 220; // matches .pdp-main img's opacity transition
  function swapMainPhoto(src) {
    if (!mainImg) return;
    var abs;
    try { abs = new URL(src, window.location.href).href; } catch (e) { abs = src; }
    if (mainImg.src === abs) return; // already showing this photo — no fade
    var token = ++mainSwapToken;
    mainImg.classList.add("is-swapping");
    var loaded = false, faded = false;
    function commit() {
      if (token !== mainSwapToken || !loaded || !faded) return;
      mainImg.src = src;
      requestAnimationFrame(function () {
        if (token === mainSwapToken) mainImg.classList.remove("is-swapping");
      });
    }
    var pre = new Image();
    pre.onload = pre.onerror = function () { loaded = true; commit(); };
    pre.src = src;
    setTimeout(function () { faded = true; commit(); }, MAIN_FADE_MS);
  }

  function setPhoto(src) {
    if (!src) return;
    currentPhotoSrc = src;
    swapMainPhoto(src);
    if (sbThumb) sbThumb.src = src;
    galleryThumbs.forEach(function (t) {
      var active = t.dataset.src === src;
      t.classList.toggle("is-active", active);
      // The rail scrolls internally (no visible scrollbar) past ~5 thumbs,
      // so bring the newly active one into view if it isn't already —
      // and peek at the next thumb below it so the rail doesn't look stuck.
      if (active) scrollThumbIntoView(t);
    });
    if (mobileTrack) {
      var slide = mobileSlides.filter(function (s) { return s.dataset.src === src; })[0];
      if (slide) {
        mobileScrollIsProgrammatic = true;
        mobileTrack.scrollTo({ left: slide.offsetLeft, behavior: "auto" });
        updateMobileProgress();
        // Released on the next frame — scrollTo's own scroll events (even
        // "auto"/instant) land asynchronously, not before this line runs.
        requestAnimationFrame(function () { mobileScrollIsProgrammatic = false; });
      }
    }
  }

  // Rebuilds the thumbnail rail from a plain [{src, thumb}, ...] list —
  // used both for the initial full gallery and for a colour's own photos.
  function renderThumbs(items, activeSrc) {
    if (!thumbsWrap) return;
    // > 0, not > 1: a colour whose photo set is a single image still gets
    // its one thumb, matching the Liquid above. .pdp-gallery is a 2-column
    // grid (thumbs | main image) and keeps the rail's column either way so
    // the main image is the same width on every PDP (see .pdp-gallery in
    // product.css) — hiding the rail here would just leave that column
    // blank. Only an empty set clears it, and emptying the markup isn't
    // enough on its own: a leftover non-empty #pdpThumbs would still paint
    // borders in column 1, so hide it outright.
    var showThumbs = items.length > 0;
    thumbsWrap.hidden = !showThumbs;
    thumbsWrap.innerHTML = showThumbs
      ? items.map(function (item, i) {
          return '<button type="button" class="pdp-thumb' + (item.src === activeSrc ? " is-active" : "") +
            '" data-thumb data-src="' + item.src + '" aria-label="View image ' + (i + 1) + '">' +
            '<img src="' + item.thumb + '" alt="" loading="lazy"></button>';
        }).join("")
      : "";
    galleryThumbs = showThumbs ? Array.prototype.slice.call(thumbsWrap.querySelectorAll("[data-thumb]")) : [];
    gallerySrcs = items.map(function (item) { return item.src; });
    if (galleryEl) galleryEl.classList.toggle("is-single", !showThumbs);
  }

  // Rebuilds the mobile carousel's slides from the same [{src, thumb}, ...]
  // list renderThumbs uses, so both stay in lockstep on colour/size change.
  function renderMobileSlides(items) {
    if (!mobileTrack) return;
    mobileTrack.innerHTML = items.map(function (item, i) {
      return '<div class="pdp-mobile-slide" data-mobile-slide data-src="' + item.src + '">' +
        '<img src="' + item.src + '" alt="" loading="' + (i === 0 ? "eager" : "lazy") + '"></div>';
    }).join("");
    mobileSlides = Array.prototype.slice.call(mobileTrack.querySelectorAll("[data-mobile-slide]"));
    mobileTrack.scrollLeft = 0;
    updateMobileProgress();
  }

  // Moves the item matching targetSrc to the front of the list — inserting
  // it as a new entry first if it isn't already part of the set. Used so
  // the exact selected variant's own assigned photo (from the Variants
  // section's single-image picker) always leads and is active, on top of
  // whatever wider photo set the colour has (from the Media section).
  function moveToFront(items, targetSrc, targetThumb) {
    if (!targetSrc) return items;
    var target = null;
    var rest = items.filter(function (item) {
      if (item.src === targetSrc) { target = item; return false; }
      return true;
    });
    if (!target) target = { src: targetSrc, thumb: targetThumb || targetSrc };
    return [target].concat(rest);
  }

  // Variant id → that exact variant's own photo range (see the
  // pdpVariantGalleryJson script tag — its own assigned image up to the
  // next variant's, in Media-grid upload order). Preferred whenever it
  // exists; a variant with no range of its own falls back to its colour's
  // bulk-assigned photos, then to the full, unfiltered gallery.
  var variantGalleries = parseJSON("pdpVariantGalleryJson") || {};
  var colorGalleries = parseJSON("pdpColorGalleryJson") || {};

  function applyVariantGallery(variant) {
    if (!thumbsWrap || !variant) return;
    var items;
    if (variantGalleries[variant.id] && variantGalleries[variant.id].length) {
      items = variantGalleries[variant.id];
    } else if (variant.color && colorGalleries[variant.color] && colorGalleries[variant.color].length) {
      items = moveToFront(colorGalleries[variant.color], variant.image, variant.thumb);
    } else {
      items = moveToFront(defaultGalleryItems, variant.image, variant.thumb);
    }
    var activeSrc = items[0] && items[0].src;
    renderThumbs(items, activeSrc);
    renderMobileSlides(items);
    setPhoto(activeSrc);
  }

  // Thumbnail clicks — delegated since the rail's own buttons get replaced
  // whenever the colour/size changes (see applyVariantGallery above).
  if (thumbsWrap) {
    thumbsWrap.addEventListener("click", function (e) {
      var thumb = e.target.closest("[data-thumb]");
      if (thumb) setPhoto(thumb.dataset.src);
    });
  }

  // Also requires the desktop-width gallery layout (matches the 900px
  // breakpoint in product.css) — without it, a real mouse resting over the
  // image while the browser window/DevTools is narrow (no touch emulation)
  // would still trigger the 2.2x hover-zoom even though the page is showing
  // its mobile layout.
  var hoverZoomCapable = window.matchMedia &&
    window.matchMedia("(hover: hover) and (pointer: fine) and (min-width: 900px)").matches;
  var mainFigure = byId("pdpMainFigure");

  /* ── Lightbox ─────────────────────────────────────────────── */
  var lightbox = byId("pdpLightbox");
  var lbImg = byId("pdpLbImg");
  var lbIndex = 0;

  function lbSet(i) {
    if (!gallerySrcs.length) return;
    lbIndex = (i + gallerySrcs.length) % gallerySrcs.length;
    var src = gallerySrcs[lbIndex];
    if (lbImg) lbImg.src = src;
    if (lightbox) {
      lightbox.querySelectorAll(".pdp-lb-thumb").forEach(function (t, j) {
        t.classList.toggle("is-active", j === lbIndex);
      });
    }
  }
  function openLightbox() {
    if (!lightbox || !mainImg) return;
    var idx = gallerySrcs.indexOf(currentPhotoSrc);
    lbSet(idx === -1 ? 0 : idx);
    lightbox.hidden = false;
    lightbox.setAttribute("aria-hidden", "false");
    document.body.style.overflow = "hidden";
  }
  function closeLightbox() {
    if (!lightbox) return;
    lightbox.hidden = true;
    lightbox.setAttribute("aria-hidden", "true");
    document.body.style.overflow = "";
  }

  if (mainFigure) {
    mainFigure.addEventListener("keydown", function (e) {
      // Let this button handle its own Enter/Space instead of also zooming.
      if (e.target.closest("#pdpCtlJump")) return;
      if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openLightbox(); }
    });

    // Hover-to-zoom (mouse/trackpad only): the image scales up and pans to
    // track the cursor, so click-to-open-lightbox is skipped on these
    // devices — hovering already reveals the detail.
    if (hoverZoomCapable && mainImg) {
      mainFigure.addEventListener("mousemove", function (e) {
        var rect = mainFigure.getBoundingClientRect();
        var x = ((e.clientX - rect.left) / rect.width) * 100;
        var y = ((e.clientY - rect.top) / rect.height) * 100;
        mainImg.style.transformOrigin = x + "% " + y + "%";
      });
      mainFigure.addEventListener("mouseenter", function () { mainImg.classList.add("is-zoomed"); });
      mainFigure.addEventListener("mouseleave", function () {
        mainImg.classList.remove("is-zoomed");
        mainImg.style.transformOrigin = "";
      });
    }
  }

  /* ── Variant selection → live price/specs/image ──────────── */
  var priceEl = byId("pdpPrice");
  var sbPriceEl = byId("pdpSbPrice");
  var variantIdInput = byId("pdpVariantId");
  var addBagBtn = byId("pdpAddBag");
  var sbAddBtn = byId("pdpSbAdd");
  var vsAddBtn = byId("pdpVsAdd");
  var addBagTextEl = byId("pdpAddBagText");
  var sbAddTextEl = byId("pdpSbAddText");
  var vsAddTextEl = byId("pdpVsAddText");
  var saleBadgeEl = byId("pdpSaleBadge");
  var newBadgeEl = byId("pdpNewBadge");
  var imageSoldoutBadgeEl = byId("pdpImageSoldoutBadge");
  var mobileSoldoutBadgeEl = byId("pdpMobileSoldoutBadge");
  var imageNewBadgeEl = byId("pdpImageNewBadge");
  var mobileNewBadgeEl = byId("pdpMobileNewBadge");
  var paylaterEl = byId("pdpPaylaterText");
  var paylaterRowEl = byId("pdpPaylater");
  // Below this price the instalment offer isn't shown at all — merchant-set
  // in the section settings, in cents so it compares against variant.price.
  var paylaterMin = paylaterRowEl ? parseInt(paylaterRowEl.dataset.minPrice, 10) || 0 : 0;
  var loyaltyTextEl = byId("pdpLoyaltyText");
  var sizeHintEl = byId("pdpSizeHint");

  /* "Low in stock: only N left." — both copies of the note (desktop size
     block + mobile variant sheet) update together. A variant Shopify
     isn't tracking has inventory:null and never triggers it, so an
     untracked line can't read as "only 0 left". Threshold 0 is off. */
  var lowStockMax = parseInt(root.dataset.lowStockThreshold, 10) || 0;

  function applyLowStock(v) {
    var qty = v && v.inventory;
    var show = lowStockMax > 0 && typeof qty === "number" && qty > 0 && qty <= lowStockMax;
    root.querySelectorAll("[data-low-stock]").forEach(function (el) {
      el.hidden = !show;
    });
    if (!show) return;
    var text = lowStockText(qty);
    root.querySelectorAll("[data-low-stock-text]").forEach(function (el) {
      el.textContent = text;
    });
  }

  function priceHTML(v) {
    if (v.compare_price_html) {
      return '<span class="pdp-was">' + v.compare_price_html + '</span><span class="pdp-now">' + v.price_html + '</span>';
    }
    return '<span class="pdp-price-val">' + v.price_html + "</span>";
  }
  function sbPriceHTML(v) {
    if (v.compare_price_html) {
      return '<span class="was">' + v.compare_price_html + '</span> <span class="now">' + v.price_html + "</span>";
    }
    return v.price_html;
  }

  function applyVariant(v) {
    applyLowStock(v);

    if (!v) {
      [addBagBtn, sbAddBtn, vsAddBtn].forEach(function (btn) { if (btn) btn.disabled = true; });
      if (addBagTextEl) addBagTextEl.textContent = "Unavailable";
      if (sbAddTextEl) sbAddTextEl.textContent = "Unavailable";
      if (vsAddTextEl) vsAddTextEl.textContent = "Unavailable";
      if (imageSoldoutBadgeEl) imageSoldoutBadgeEl.hidden = false;
      if (mobileSoldoutBadgeEl) mobileSoldoutBadgeEl.hidden = false;
      if (imageNewBadgeEl) imageNewBadgeEl.hidden = true;
      if (mobileNewBadgeEl) mobileNewBadgeEl.hidden = true;
      return;
    }

    if (variantIdInput) variantIdInput.value = v.id;
    if (priceEl) {
      priceEl.innerHTML = priceHTML(v);
      priceEl.classList.toggle("is-sale", !!v.compare_price_html);
    }
    var available = !!v.available;
    if (saleBadgeEl) saleBadgeEl.hidden = !v.compare_price_html;
    if (imageSoldoutBadgeEl) imageSoldoutBadgeEl.hidden = available;
    if (mobileSoldoutBadgeEl) mobileSoldoutBadgeEl.hidden = available;
    // "New" shares the above-brand slot with Sale — when Sale claims it,
    // "New" moves onto the product image instead (see sections/product.liquid).
    if (newBadgeEl) newBadgeEl.hidden = !!v.compare_price_html;
    if (imageNewBadgeEl) imageNewBadgeEl.hidden = !available || !v.compare_price_html;
    if (mobileNewBadgeEl) mobileNewBadgeEl.hidden = !available || !v.compare_price_html;
    if (sbPriceEl) sbPriceEl.innerHTML = sbPriceHTML(v);

    if (addBagBtn) {
      addBagBtn.disabled = !available;
      if (addBagTextEl) addBagTextEl.textContent = available
        ? (T.add_to_bag || "Add to bag")
        : (T.sold_out || "Sold Out");
    }
    // The sticky bar was the one add button left saying "Add to bag" on a
    // sold-out variant — disabled and faded, but still reading as the offer.
    if (sbAddBtn) {
      sbAddBtn.disabled = !available;
      if (sbAddTextEl) sbAddTextEl.textContent = available
        ? (T.add_to_bag || "Add to bag")
        : (T.sold_out || "Sold Out");
    }
    if (vsAddBtn) {
      vsAddBtn.disabled = !available;
      if (vsAddTextEl) vsAddTextEl.textContent = available
        ? (T.add_to_bag || "Add to bag")
        : (T.sold_out || "Sold Out");
    }

    // Pay-later/loyalty copy keeps its merchant-configured wording — only
    // the %%AMOUNT%%/%%POINTS%% placeholder swaps (see sections/product.liquid).
    if (paylaterEl && paylaterEl.dataset.template) {
      paylaterEl.innerHTML = paylaterEl.dataset.template.replace("%%AMOUNT%%", v.installment_html);
    }
    if (paylaterRowEl) paylaterRowEl.hidden = v.price < paylaterMin;
    if (loyaltyTextEl && loyaltyTextEl.dataset.template) {
      loyaltyTextEl.innerHTML = loyaltyTextEl.dataset.template.replace("%%POINTS%%", v.points.toLocaleString());
    }

    var skuEl = byId("pdpSpecSku");
    if (skuEl) skuEl.textContent = v.sku || "—";
    var colourSpecEl = byId("pdpSpecColour");
    if (colourSpecEl && v.color) colourSpecEl.textContent = v.color;
    var sizeSpecEl = byId("pdpSpecSize");
    if (sizeSpecEl && v.size) sizeSpecEl.textContent = v.size;

    if (v.image) setPhoto(v.image);

    // ?variant= plus a change event on the hidden input are the two hooks
    // storefront apps watch to follow the selection — the back-in-stock
    // block under the picker needs to know *which* variant is sold out, and
    // it can't read this file's state. replaceState, not pushState: a size
    // tap shouldn't cost the shopper a Back press to leave the page.
    if (window.history && history.replaceState && window.URL) {
      try {
        var variantUrl = new URL(window.location.href);
        variantUrl.searchParams.set("variant", v.id);
        history.replaceState({}, "", variantUrl.toString());
      } catch (e) {}
    }
    if (variantIdInput) {
      variantIdInput.dispatchEvent(new Event("change", { bubbles: true }));
    }
  }

  // The first option still waiting on a choice, or 0 if the shopper has
  // picked everything. Every rendered option gates the add — a shoe with
  // a Width option is as unorderable without it as one without a size.
  function missingOption() {
    for (var i = 0; i < optionPositions.length; i++) {
      if (!state.selected[optionPositions[i]]) return optionPositions[i];
    }
    return 0;
  }

  function flagMissingOption(pos) {
    var label = (optionNames[pos] || "option").toLowerCase();
    var message = selectOptionText(label);
    if (sizeHintEl) {
      sizeHintEl.textContent = message;
      sizeHintEl.hidden = false;
    }
    if (vsSizeHintEl) vsSizeHintEl.textContent = message;

    // On mobile the inline blocks are hidden behind the variant sheet, so
    // scrolling one into view would point at nothing — open the sheet on
    // the option the shopper still has to pick instead.
    if (varSheet && isMobileViewport()) {
      if (vsSizeHintEl) vsSizeHintEl.hidden = false;
      openVarSheet();
      return;
    }
    var wrap = root.querySelector('.pdp-option-block[data-opt-block="' + pos + '"] .pdp-sizes, ' +
      '.pdp-option-block[data-opt-block="' + pos + '"] .pdp-swatches');
    if (wrap) {
      wrap.classList.remove("shake");
      void wrap.offsetWidth; // restart the animation
      wrap.classList.add("shake");
      wrap.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }

  /* ── Reviews — session-only demo (no reviews app connected): seeded from
     the JSON below, new submissions live only in memory and reset on
     reload. Markup (and the note spelling this out) lives in
     sections/pdp-reviews.liquid; absent when that section isn't in the
     template, in which case everything below is a no-op. ── */
  var reviewsBody = byId("pdpReviewsBody");
  var reviewState = { reviews: parseJSON("pdpReviewsSeed") || [], formRating: 0 };

  function starSVG(on) {
    return '<svg class="pdp-star' + (on ? " is-on" : "") + '" width="16" height="16" viewBox="0 0 24 24" ' +
      'stroke="currentColor" stroke-width="1.4" stroke-linejoin="round">' +
      '<path d="M12 2.8l2.85 5.77 6.37.93-4.61 4.49 1.09 6.34L12 17.85 6.31 20.33l1.09-6.34L2.79 9.5l6.37-.93z"/></svg>';
  }
  function starsHTML(rating, cls) {
    var full = Math.round(rating), s = "";
    for (var i = 1; i <= 5; i++) s += starSVG(i <= full);
    return '<span class="pdp-stars' + (cls ? " " + cls : "") + '" role="img" aria-label="' +
      (T.rv_out_of_5 || "__RATING__ out of 5 stars").replace("__RATING__", rating) +
      '">' + s + "</span>";
  }
  function reviewsEmptyHTML() {
    var doc = '<svg class="pdp-review-icon" width="42" height="42" viewBox="0 0 24 24" fill="none" ' +
      'stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round">' +
      '<path d="M7 2.6h6.5L18 7v12.2a1.2 1.2 0 0 1-1.2 1.2H8.6"/>' +
      '<path d="M13.4 2.6V7H18"/>' +
      '<path d="M9.5 11h5M9.5 13.8h3.4"/>' +
      '<path d="M8.6 20.4 6 21.4l.4-2.9V5a2.4 2.4 0 0 1 2.4-2.4"/></svg>';
    return '<div class="pdp-review-card">' +
        '<div class="pdp-review-prompt">' + doc + '<span>What do you think about<br>this product ?</span></div>' +
        '<button type="button" class="pdp-review-btn" data-review-open>Write a review</button>' +
      "</div>";
  }
  function reviewCardHTML(r) {
    return '<li class="pdp-rev-item">' +
        '<div class="pdp-rev-item-top">' + starsHTML(r.rating) +
          (r.title ? '<span class="pdp-rev-title">' + escapeHtml(r.title) + "</span>" : "") + "</div>" +
        '<p class="pdp-rev-body">' + escapeHtml(r.body) + "</p>" +
        '<div class="pdp-rev-meta">' +
          '<span class="pdp-rev-author">' + escapeHtml(r.name) + "</span>" +
          (r.verified ? '<span class="pdp-rev-verified">Verified purchase</span>' : "") +
          '<span class="pdp-rev-date">' + escapeHtml(r.date) + "</span>" +
        "</div>" +
      "</li>";
  }
  function reviewsBodyHTML() {
    var rs = reviewState.reviews;
    if (!rs.length) return reviewsEmptyHTML();

    var sum = rs.reduce(function (a, r) { return a + r.rating; }, 0);
    var avg = sum / rs.length;
    var dist = [0, 0, 0, 0, 0];
    rs.forEach(function (r) { dist[r.rating - 1]++; });

    var bars = "";
    for (var star = 5; star >= 1; star--) {
      var count = dist[star - 1];
      var pct = Math.round((count / rs.length) * 100);
      bars += '<div class="pdp-dist-row">' +
        '<span class="pdp-dist-label">' + star + " " + starSVG(true) + "</span>" +
        '<span class="pdp-dist-track"><span class="pdp-dist-fill" style="width:' + pct + '%"></span></span>' +
        '<span class="pdp-dist-count">' + count + "</span>" +
      "</div>";
    }

    return '<div class="pdp-rev-summary">' +
        '<div class="pdp-rev-score">' +
          '<span class="pdp-rev-avg">' + avg.toFixed(1) + "</span>" +
          starsHTML(avg, "lg") +
          '<span class="pdp-rev-count">Based on ' + rs.length + " review" + (rs.length === 1 ? "" : "s") + "</span>" +
          '<button type="button" class="pdp-review-btn sm" data-review-open>Write a review</button>' +
        "</div>" +
        '<div class="pdp-rev-dist">' + bars + "</div>" +
      "</div>" +
      '<ul class="pdp-rev-list">' + rs.map(reviewCardHTML).join("") + "</ul>";
  }
  function renderReviews() {
    if (reviewsBody) reviewsBody.innerHTML = reviewsBodyHTML();
  }
  renderReviews();

  var reviewModal = byId("pdpReviewModal");
  function paintFormStars() {
    if (!reviewModal) return;
    reviewModal.querySelectorAll(".pdp-star-btn .pdp-star").forEach(function (star, i) {
      star.classList.toggle("is-on", i < reviewState.formRating);
    });
  }
  function openReview() {
    if (!reviewModal) return;
    reviewModal.hidden = false;
    reviewModal.setAttribute("aria-hidden", "false");
    document.body.style.overflow = "hidden";
  }
  function closeReview() {
    if (!reviewModal) return;
    reviewModal.hidden = true;
    reviewModal.setAttribute("aria-hidden", "true");
    document.body.style.overflow = "";
  }
  function submitReview() {
    var rating = reviewState.formRating;
    var nameEl = byId("pdpRvName"), titleEl = byId("pdpRvTitle"), bodyEl = byId("pdpRvBody");
    var name = (nameEl.value || "").trim();
    var title = (titleEl.value || "").trim();
    var body = (bodyEl.value || "").trim();
    var err = byId("pdpRvError");
    if (!rating || body.length < 3) { if (err) err.hidden = false; return; }
    if (err) err.hidden = true;
    reviewState.reviews.unshift({
      name: name || T.anonymous || "Anonymous",
      rating: rating,
      title: title,
      body: body,
      verified: true,
      date: T.rv_just_now || "Just now"
    });
    reviewState.formRating = 0;
    paintFormStars();
    nameEl.value = "";
    titleEl.value = "";
    bodyEl.value = "";
    renderReviews();
    closeReview();
    if (reviewsBody) reviewsBody.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  /* ── Mozoon loyalty drawer — decorative preview content only ─── */
  var mzDrawer = byId("pdpMzDrawer");
  var mzScrim = byId("pdpMzScrim");
  function openMozoon() {
    if (!mzDrawer || !mzScrim) return;
    mzDrawer.hidden = false;
    mzScrim.hidden = false;
    void mzDrawer.offsetWidth; // commit the off-canvas state so the slide animates
    mzDrawer.classList.add("is-open");
    mzScrim.classList.add("is-open");
    mzDrawer.setAttribute("aria-hidden", "false");
    document.body.style.overflow = "hidden";
  }
  function closeMozoon() {
    if (!mzDrawer || !mzScrim) return;
    mzDrawer.classList.remove("is-open");
    mzScrim.classList.remove("is-open");
    mzDrawer.setAttribute("aria-hidden", "true");
    document.body.style.overflow = "";
    var onEnd = function () {
      mzDrawer.hidden = true;
      mzScrim.hidden = true;
      mzDrawer.removeEventListener("transitionend", onEnd);
    };
    mzDrawer.addEventListener("transitionend", onEnd);
  }

  /* ── Variant sheet (mobile) ───────────────────────────────────
     The bottom sheet behind the .pdp-optbar cells. Its swatches and
     chips are a second print of the same markup the inline blocks use
     (see the capture in sections/product.liquid), so the delegated
     [data-swatch]/[data-size] handlers below already keep both copies
     in sync — all this has to own is open/close plus the summary text
     on the bar itself. ── */
  var varSheet = byId("pdpVarSheet");
  var varScrim = byId("pdpVarSheetScrim");
  var vsSizeHintEl = byId("pdpVsSizeHint");

  /* The @app outlet (see sections/product.liquid) is a single node, and it
     has to be: an app block can't be rendered twice, and a second copy
     would duplicate whatever ids and listeners the app hangs off it. So the
     one node commutes. It lives under the buy row in the panel, and rides
     into the sheet's footer while the sheet is open — on mobile the panel's
     buy row is hidden entirely and this sheet is where the shopper meets
     "Sold Out", so a back-in-stock button that stayed behind in the panel
     would be answering a question asked on a different screen. Moving a
     node keeps its listeners, so the app is none the wiser. A marker
     comment holds the original spot rather than trusting sibling lookups,
     which would drift if an app ever reordered anything around it. */
  var appBlocks = root.querySelector(".pdp-app-blocks");
  var vsFoot = varSheet ? varSheet.querySelector(".pdp-vs-foot") : null;
  var appBlocksMarker = null;
  if (appBlocks && vsFoot) {
    appBlocksMarker = document.createComment("pdp-app-blocks");
    appBlocks.parentNode.insertBefore(appBlocksMarker, appBlocks);
  }

  function moveAppBlocks(intoSheet) {
    if (!appBlocksMarker) return;
    if (intoSheet) vsFoot.appendChild(appBlocks);
    else appBlocksMarker.parentNode.insertBefore(appBlocks, appBlocksMarker);
  }

  // A close in flight owns two things that have to be called off if the
  // sheet is reopened before it lands (tap a picker cell again inside the
  // 600ms) — otherwise the stale finisher hides a sheet that's on its way
  // back in and drags the @app outlet out of its footer.
  var vsCloseTimer = null;
  var vsCloseEnd = null;

  function cancelPendingSheetClose() {
    if (vsCloseTimer) { clearTimeout(vsCloseTimer); vsCloseTimer = null; }
    if (vsCloseEnd) {
      varSheet.removeEventListener("transitionend", vsCloseEnd);
      vsCloseEnd = null;
    }
  }

  function openVarSheet() {
    if (!varSheet || !varScrim) return;
    cancelPendingSheetClose();
    varSheet.hidden = false;
    varScrim.hidden = false;
    void varSheet.offsetWidth; // commit the off-canvas state so the slide animates
    varSheet.classList.add("is-open");
    varScrim.classList.add("is-open");
    varSheet.setAttribute("aria-hidden", "false");
    document.body.style.overflow = "hidden";
    moveAppBlocks(true);

    // Both pickers are one-row scrolling strips, so the current pick can
    // sit well off to the right — centre it rather than always opening on
    // the first chip. scrollLeft, not scrollIntoView: the latter would
    // also scroll the sheet (and the page behind it) vertically.
    varSheet.querySelectorAll(".pdp-vs-colours, .pdp-vs-sizes").forEach(function (strip) {
      var active = strip.querySelector(".is-active");
      if (!active) return;
      strip.scrollLeft = active.offsetLeft - (strip.clientWidth - active.offsetWidth) / 2;
    });
  }

  function closeVarSheet() {
    if (!varSheet || !varScrim) return;
    varSheet.classList.remove("is-open");
    varScrim.classList.remove("is-open");
    varSheet.setAttribute("aria-hidden", "true");
    document.body.style.overflow = "";
    // transitionend bubbles, so a transition on anything inside the sheet
    // would otherwise finish the close early — and the @app outlet now
    // rides out with it, which would mean yanking the button back
    // mid-slide. The timer is the other half of that: miss the event
    // entirely (no transition ran, the tab was backgrounded) and the outlet
    // would be stranded in a hidden sheet, gone from the page until the
    // next open.
    cancelPendingSheetClose();
    vsCloseEnd = function (e) {
      if (e && e.target !== varSheet) return;
      cancelPendingSheetClose();
      varSheet.hidden = true;
      varScrim.hidden = true;
      moveAppBlocks(false);
    };
    vsCloseTimer = setTimeout(vsCloseEnd, 600);
    varSheet.addEventListener("transitionend", vsCloseEnd);
  }

  /* ── Size guide overlay (products with a Size option) ─────────
     A full-screen measurement panel. The table body is built here
     from #pdpSizeGuideData; the chart / unit / region toggles just
     update sgState and re-render. Root is-product/is-body/is-cm/is-in
     classes drive the static copy + switch visuals (assets/product.css).
     Absent (null overlay) on products without a Size option, so
     every handler below no-ops safely there. ── */
  var sgOverlay = byId("pdpSizeGuide");
  var sgTable = byId("pdpSgTable");
  var sgData = null;
  var sgState = { chart: "product", unit: "cm", region: "EU" };

  if (sgOverlay && sgTable) {
    var sgSeed = byId("pdpSizeGuideData");
    if (sgSeed) {
      try { sgData = JSON.parse(sgSeed.textContent); } catch (err) { sgData = null; }
    }
  }

  // One decimal place, bare in both units: the column header carries the
  // unit ("Chest (in)"). Values are stored per unit in the JSON, so this
  // only formats — it never converts.
  function sgFmt(value) {
    // Size-system cells (Cole Haan's EU / UK columns) arrive as strings.
    if (typeof value === "string") return value;
    // Whole numbers print bare (94, not 94.0); halves keep theirs (36.5).
    return (Math.round(value * 10) / 10).toFixed(1).replace(/\.0$/, "");
  }

  // A measurement column is one whose values change with the CM / IN
  // switch; size-system columns (EU, UK, "Bra size") read the same in both.
  function sgIsMeasured(chart, i) {
    return chart.rows.some(function (row) {
      return row.cm && row.in && String(row.cm[i]) !== String(row.in[i]);
    });
  }

  function renderSizeGuide() {
    if (!sgData || !sgTable) return;
    var chart = sgData[sgState.chart];
    if (!chart) return;
    var unit = sgState.unit;

    var head = "<thead><tr><th>" + (chart.sizeLabel || "Size") + "</th>";
    // Body chart carries a size-system column (EU / UK / US) after Size.
    if (sgState.chart === "body") head += "<th>" + sgState.region + "</th>";
    // Measured headers name the unit ("Height (cm)"), in the page's language.
    var unitName = (sgOverlay && sgOverlay.dataset[unit === "in" ? "unitIn" : "unitCm"]) || unit;
    chart.columns.forEach(function (col, i) {
      head += "<th>" + col + (sgIsMeasured(chart, i) ? " (" + unitName + ")" : "") + "</th>";
    });
    head += "</tr></thead>";

    var body = "<tbody>";
    chart.rows.forEach(function (row) {
      body += "<tr><td>" + row.size + "</td>";
      if (sgState.chart === "body") {
        var sys = row[sgState.region.toLowerCase()] || row.eu;
        body += "<td>" + sys + "</td>";
      }
      (row[unit] || []).forEach(function (v) { body += "<td>" + sgFmt(v) + "</td>"; });
      body += "</tr>";
    });
    body += "</tbody>";

    sgTable.innerHTML = head + body;
  }

  // Right-side drawer, same open/close mechanics as the Mozoon drawer:
  // unhide + force a reflow so the off-canvas transform is committed, then
  // slide in; on close, slide out and hide once the transform settles.
  var sgScrim = byId("pdpSgScrim");

  function openSizeGuide() {
    if (!sgOverlay) return;
    renderSizeGuide();
    sgOverlay.classList.remove("is-guide"); // always open on the chart, not the guide sub-view
    sgOverlay.hidden = false;
    if (sgScrim) sgScrim.hidden = false;
    void sgOverlay.offsetWidth; // commit the off-canvas state so the slide animates
    sgOverlay.classList.add("is-open");
    if (sgScrim) sgScrim.classList.add("is-open");
    sgOverlay.setAttribute("aria-hidden", "false");
    document.body.style.overflow = "hidden";
    sgOverlay.scrollTop = 0;
  }

  function closeSizeGuide() {
    if (!sgOverlay) return;
    sgOverlay.classList.remove("is-open");
    if (sgScrim) sgScrim.classList.remove("is-open");
    sgOverlay.setAttribute("aria-hidden", "true");
    document.body.style.overflow = "";
    var onEnd = function (ev) {
      // Only the drawer's own slide counts — ignore hover transitions that
      // bubble up from buttons inside it — and bail if it was reopened
      // mid-close.
      if (ev && ev.target !== sgOverlay) return;
      if (sgOverlay.classList.contains("is-open")) return;
      sgOverlay.hidden = true;
      if (sgScrim) sgScrim.hidden = true;
      sgOverlay.removeEventListener("transitionend", onEnd);
    };
    sgOverlay.addEventListener("transitionend", onEnd);
    if (window.location.hash === "#size-guide" && window.history && window.history.replaceState) {
      window.history.replaceState(null, "", window.location.pathname + window.location.search);
    }
  }

  function setSgChart(chart) {
    if (!sgOverlay) return;
    sgState.chart = chart;
    sgOverlay.classList.toggle("is-product", chart === "product");
    sgOverlay.classList.toggle("is-body", chart === "body");
    sgOverlay.querySelectorAll("[data-sg-chart-btn]").forEach(function (btn) {
      var on = btn.dataset.sgChartBtn === chart;
      btn.classList.toggle("is-active", on);
      btn.setAttribute("aria-pressed", on ? "true" : "false");
    });
    renderSizeGuide();
  }

  function setSgUnit(unit) {
    if (!sgOverlay) return;
    sgState.unit = unit;
    sgOverlay.classList.toggle("is-cm", unit === "cm");
    sgOverlay.classList.toggle("is-in", unit === "in");
    var sw = sgOverlay.querySelector("[data-sg-unit-toggle]");
    if (sw) sw.setAttribute("aria-checked", unit === "cm" ? "true" : "false");
    renderSizeGuide();
  }

  var sgRegionSel = byId("pdpSgRegion");
  if (sgRegionSel) {
    sgRegionSel.addEventListener("change", function () {
      sgState.region = sgRegionSel.value;
      renderSizeGuide();
    });
  }

  /* Crosshair hover: hovering a cell traces navy back to that row's
     Size/region label(s) and up its column header, so the value's size +
     measurement read at a glance. Pure CSS can't target a column or the
     cells above, so it's done here. Re-render replaces the table's innards,
     which clears any stale highlight; the listener lives on the table
     element itself, which persists. */
  function sgClearHover() {
    if (!sgTable) return;
    sgTable.querySelectorAll(".sg-hit").forEach(function (el) {
      el.classList.remove("sg-hit");
    });
  }

  function sgApplyHover(cell) {
    var tbody = sgTable.tBodies[0];
    if (!tbody) return;
    sgClearHover();
    var tr = cell.parentNode;
    var rows = tbody.rows;
    var col = cell.cellIndex;

    // Full column: the header + every data row in this column.
    var head = sgTable.tHead;
    if (head && head.rows[0] && head.rows[0].cells[col]) head.rows[0].cells[col].classList.add("sg-hit");
    for (var r = 0; r < rows.length; r++) {
      if (rows[r].cells[col]) rows[r].cells[col].classList.add("sg-hit");
    }
    // Hovered row: from the first column up to and including the hovered cell.
    for (var c = 0; c <= col; c++) {
      if (tr.cells[c]) tr.cells[c].classList.add("sg-hit");
    }
  }

  if (sgTable && window.matchMedia && window.matchMedia("(hover: hover)").matches) {
    sgTable.addEventListener("mouseover", function (e) {
      var cell = e.target.closest("td");
      var tb = sgTable.tBodies[0];
      if (cell && tb && tb.contains(cell)) sgApplyHover(cell);
    });
    sgTable.addEventListener("mouseleave", sgClearHover);
  }

  /* Mirror the current pick onto every readout for that option — the
     desktop block head, the collapsed bar cell and the sheet heading all
     carry data-opt-out, so one pass covers all three however many
     options the product has. Each supplies its own empty-state wording
     via data-opt-fallback ("Select" on the bar, "—" in a heading).
     The colour dot borrows the active swatch's thumbnail so the bar
     shows the actual fabric, not an approximated flat colour. */
  function syncOptBar() {
    optionPositions.forEach(function (pos) {
      var value = state.selected[pos] || null;
      root.querySelectorAll('[data-opt-out="' + pos + '"]').forEach(function (el) {
        el.textContent = value || el.dataset.optFallback || "Select";
      });
    });

    var dot = byId("pdpOptBarDot");
    var activeSwatch = root.querySelector("[data-swatch].is-active");
    if (dot && activeSwatch) {
      var thumb = activeSwatch.dataset.thumb || activeSwatch.dataset.image;
      if (thumb) {
        dot.style.backgroundImage = "url('" + thumb + "')";
      } else if (activeSwatch.style.backgroundColor) {
        dot.style.backgroundImage = "";
        dot.style.backgroundColor = activeSwatch.style.backgroundColor;
      }
    }
  }

  /* ── Share ────────────────────────────────────────────────── */
  (function initShare() {
    /* Never window.location.href on its own: under `shopify theme dev` that's
       http://127.0.0.1:9292/... and on a theme preview it's the *.myshopify.com
       host — a link the recipient can't open, and one WhatsApp won't even turn
       into a link (it doesn't linkify bare IPs). sections/product.liquid stamps
       the storefront's own origin, and the current path and query ride on top
       so the selected variant (see the ?variant= sync in applyVariant) is still
       part of what gets shared. */
    var shareEl = byId("pdpShare");
    var origin = shareEl && shareEl.dataset.shareOrigin;
    var url = origin
      ? origin.replace(/\/+$/, "") + window.location.pathname + window.location.search
      : window.location.href;
    var text = document.title;
    var wa = byId("pdpShareWhatsapp");
    var fb = byId("pdpShareFacebook");
    var mail = byId("pdpShareEmail");
    if (wa) wa.href = "https://wa.me/?text=" + encodeURIComponent(text + " " + url);
    if (fb) fb.href = "https://www.facebook.com/sharer/sharer.php?u=" + encodeURIComponent(url);
    if (mail) mail.href = "mailto:?subject=" + encodeURIComponent(text) + "&body=" + encodeURIComponent(text + "\n\n" + url);

    /* The Instagram button shares this same canonical URL, so it cannot build
       its own href up front like the three above — it has no endpoint to point
       at. Park the URL where the delegated click handler can read it. */
    if (shareEl) shareEl.dataset.shareUrl = url;

    /* Instagram has no share endpoint, so that button is the system share sheet
       or it is nothing — and it is only something on a phone. "Does the
       browser have navigator.share?" was the old test, and it was the wrong
       one: desktop Chrome and Edge on Windows do have it, and it opens the
       Windows share flyout, which has no Instagram in it. So the icon showed
       on desktop and did nothing useful, and it appeared or vanished between
       browsers for reasons no shopper could see. useNativeSheet() asks the
       question that matters — is this a touch device, where the sheet lists
       the installed apps, Instagram included. */
    var ig = shareEl && shareEl.querySelector("[data-share-instagram]");
    if (ig && !useNativeSheet()) ig.hidden = true;
  })();

  /* ── Related products — Shopify's product recommendations, fetched
     async and mounted with real PLP cards (snippets/product-card.liquid)
     rendered by sections/product-recommendations.liquid, so "You may
     also like" looks and behaves exactly like the listing page. The
     mount and the fetch URL both live in sections/pdp-related.liquid —
     absent (and this a no-op) if that section isn't in the template. The
     grid is #plpRoot, so assets/listing.js's own delegated handlers
     (swatches, quick add / Quick View, wishlist, dots) drive it — this
     just runs the same idempotent post-injection passes listing.js's
     own AJAX refresh/infinite-scroll use on newly-appended cards.

     The grid is also a horizontal slider (blue-salon.js's initSlider,
     same pattern as sections/product-grid.liquid) whose arrows are
     wired up at page load, before these cards exist — its scroll-
     position check (which enables/disables the arrows) only re-runs on
     scroll/resize, so a synthetic resize event nudges it to recheck now
     that the grid has something to scroll. */
  (function loadRelated() {
    var mount = byId("pdpRelated");
    var grid = byId("plpRoot");
    if (!mount || !grid) return;
    var url = mount.dataset.recommendationsUrl;
    if (!url) return;
    fetch(url)
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var temp = document.createElement("div");
        temp.innerHTML = html;
        var cards = temp.querySelectorAll(".plp-card");
        if (!cards.length) return;
        cards.forEach(function (card) { grid.appendChild(card); });
        mount.hidden = false;
        hydrateWishlist();
        if (window.BSListing) window.BSListing.enhanceCards(grid);
        window.dispatchEvent(new Event("resize"));
      })
      .catch(function () {});
  })();

  /* ── Sticky bars ──────────────────────────────────────────────
     Four scroll signals feed syncStickyBar(), which owns every class
     on both bars — the same scroll positions mean different things per
     viewport, so nothing toggles them at its own observation site:

       .pdp-buy scrolled past       → desktop reveal (slides down from top)
       reveal anchor reached        → mobile reveal (slides up from bottom)
       mobile gallery scrolled past → mobile price expansion (.is-visible),
                                      and the #pdpStickyTop back bar
       footer in view               → mobile: both bars back out

     Mobile deliberately waits for the variant bar rather than showing
     from load (a permanent Add to bag over the opening product shot
     covers the photo before the shopper has picked anything) and stands
     down again at the footer, where a buy bar for a product that's now
     scrolled well out of sight is just covering the newsletter and app
     links. Whichever observers are meaningless for the current viewport
     watch a display:none element, so they fire once at init and stay
     false. */
  var stickyBar = byId("pdpStickyBar");
  var stickyTop = byId("pdpStickyTop");
  if (stickyBar && "IntersectionObserver" in window) {
    var buyRowGone = false;   // desktop: the in-panel buy row is off-screen
    var galleryGone = false;  // mobile: the product shot is off-screen
    var pastAnchor = false;   // mobile: the variant bar has been reached
    var atFooter = false;     // mobile: the footer has come into view

    function syncStickyBar() {
      var mobile = isMobileViewport();
      var ended = mobile && atFooter;
      var barShown = mobile ? (pastAnchor && !ended) : buyRowGone;
      stickyBar.classList.toggle("is-revealed", mobile && barShown);
      stickyBar.classList.toggle("is-visible", mobile ? galleryGone : buyRowGone);
      stickyBar.setAttribute("aria-hidden", barShown ? "false" : "true");

      if (stickyTop) {
        var topShown = galleryGone && !ended;
        stickyTop.classList.toggle("is-visible", topShown);
        stickyTop.setAttribute("aria-hidden", topShown ? "false" : "true");
      }
    }

    var observeScrolledPast = function (el, onChange) {
      if (!el) return;
      new IntersectionObserver(function (entries) {
        var en = entries[0];
        onChange(!en.isIntersecting && en.boundingClientRect.top < 0);
        syncStickyBar();
      }, { threshold: 0 }).observe(el);
    };

    observeScrolledPast(root.querySelector(".pdp-buy"), function (v) { buyRowGone = v; });
    observeScrolledPast(root.querySelector(".pdp-mobile-gallery"), function (v) { galleryGone = v; });

    /* The variant bar is the point the bottom bar waits for; products
       with no options have none, so the gold rule that always precedes
       the buy area stands in.

       A scroll read rather than more observers, because both of these
       anchors sit mid-page: jumping straight from below one back to the
       top (an iOS status-bar tap, a bfcache restore) moves it from above
       the viewport to below it without ever crossing an intersection
       threshold, so an observer would simply never fire and the bars
       would stay stuck open. The 80px keeps the reveal from firing the
       instant the anchor's top edge clips the viewport. */
    var revealAnchor = root.querySelector(".pdp-optbar") || root.querySelector(".pdp-rule");
    var footerEl = document.querySelector("footer.bs-footer") || document.querySelector("footer");
    if (revealAnchor || footerEl) {
      var stickyRaf = null;
      var readStickyScroll = function () {
        stickyRaf = null;
        var changed = false;
        if (revealAnchor) {
          var reached = revealAnchor.getBoundingClientRect().top < window.innerHeight - 80;
          if (reached !== pastAnchor) { pastAnchor = reached; changed = true; }
        }
        if (footerEl) {
          var footerUp = footerEl.getBoundingClientRect().top < window.innerHeight;
          if (footerUp !== atFooter) { atFooter = footerUp; changed = true; }
        }
        if (changed) syncStickyBar();
      };
      var queueStickyScroll = function () {
        if (stickyRaf === null) stickyRaf = requestAnimationFrame(readStickyScroll);
      };
      window.addEventListener("scroll", queueStickyScroll, { passive: true });
      window.addEventListener("resize", queueStickyScroll, { passive: true });
      readStickyScroll();
    }

    syncStickyBar();
  }

  /* ── Back button(s): breadcrumb + sticky top bar ──────────────
     Two instances share the same data-pdp-back hook and behaviour: the
     always-visible one in .pdp-crumbs, and the mobile-only one in the
     sticky top bar once it's scrolled into view. Bound directly (not
     delegated) since there are only ever the two of them. A short fade
     (body.pdp-leaving) before the delayed navigation so the tap reads as a
     deliberate transition instead of an instant, jarring cut.

     history.back() alone isn't enough, and failed loudly: open a shared link
     from WhatsApp (or any in-app browser, or a new tab) and this page is the
     first entry in that tab's history, so back() has nowhere to go and does
     nothing at all — leaving the body faded to opacity 0 and the page looking
     blank. So back() is only used when the previous page was actually ours;
     otherwise it walks up the breadcrumb instead, which is where the arrow
     visually points anyway. */
  var pdpBackBtns = document.querySelectorAll("[data-pdp-back]");
  if (pdpBackBtns.length) {
    // The last breadcrumb link: the collection when the shopper came through
    // one, otherwise Home. Read from the DOM so it can't drift from the trail
    // rendered above it (see .pdp-crumbs in sections/product.liquid).
    function backFallbackUrl() {
      var links = document.querySelectorAll(".pdp-crumbs a[href]");
      return links.length ? links[links.length - 1].href : "/";
    }

    function cameFromThisSite() {
      if (!document.referrer) return false;
      try {
        return new URL(document.referrer).origin === window.location.origin;
      } catch (e) {
        return false;
      }
    }

    function leavePdp() {
      var fallback = backFallbackUrl();
      if (!cameFromThisSite()) {
        window.location.href = fallback;
        return;
      }
      /* Same-origin referrer still doesn't guarantee a history entry — a
         target="_blank" link from our own PLP opens a fresh tab with one. If
         back() hasn't started unloading us shortly, take the breadcrumb and
         drop the fade so the page can never be left invisible. */
      var watchdog = setTimeout(function () {
        document.body.classList.remove("pdp-leaving");
        window.location.href = fallback;
      }, 400);
      window.addEventListener("pagehide", function () {
        clearTimeout(watchdog);
      }, { once: true });
      history.back();
    }

    Array.prototype.forEach.call(pdpBackBtns, function (btn) {
      btn.addEventListener("click", function (e) {
        e.preventDefault();
        var reduceMotion = window.matchMedia &&
          window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        document.body.classList.add("pdp-leaving");
        setTimeout(leavePdp, reduceMotion ? 0 : 160);
      });
    });
    // Clears the fade on both a fresh load and a bfcache restore, so a
    // page that faded out right before navigating away never comes back
    // (via the browser's forward button) stuck invisible.
    window.addEventListener("pageshow", function () {
      document.body.classList.remove("pdp-leaving");
    });
  }

  /* ── Sticky top bar: bag button ────────────────────────────────
     Defers to the site-wide drawer in assets/blue-salon.js via
     window.BS_openCartDrawer — it stays open, unlike
     window.BS_showCartPreview's auto-dismissing preview mode — passing
     this bar's own icon as the anchor so the popover hangs off it.
     Clicking the header's #cartOpenBtn is the fallback if that API
     isn't there, and href="{{ routes.cart_url }}" on the link itself
     backs that up if the header button isn't found either. */
  if (stickyTop) {
    var sttCart = stickyTop.querySelector(".pdp-stt-cart");
    if (sttCart) {
      sttCart.addEventListener("click", function (e) {
        // Anchors the popover to this icon rather than the header's. By
        // the time this bar is on screen the header has scrolled away, so
        // positioning off #cartOpenBtn put the drawer outside the
        // viewport — it opened, but nothing was visible.
        if (window.BS_openCartDrawer) {
          e.preventDefault();
          window.BS_openCartDrawer(sttCart);
          return;
        }
        var headerCartBtn = document.getElementById("cartOpenBtn");
        if (headerCartBtn) {
          e.preventDefault();
          headerCartBtn.click();
        }
      });
    }
  }

  /* "Link copied" confirmation for the copy fallback below (bug #8).

     A checkmark swapped into a 20px icon was the only feedback, and QA
     reported it as no feedback at all on Firefox desktop and iOS — the two
     places the copy path actually runs. One toast element, built on first
     use and reused, so repeated taps restart the timer rather than stacking.

     role="status" makes it a polite live region: a screen reader announces
     "Link copied" without interrupting, which the icon swap never did. */
  var shareToast = null;
  var shareToastTimer = null;
  function showShareToast(message) {
    if (!shareToast) {
      shareToast = document.createElement("div");
      shareToast.className = "pdp-share-toast";
      shareToast.setAttribute("role", "status");
      shareToast.setAttribute("aria-live", "polite");
      document.body.appendChild(shareToast);
    }
    // Cleared first so an identical repeat message is still re-announced.
    shareToast.textContent = "";
    shareToast.classList.remove("is-visible");
    void shareToast.offsetWidth; // restart the fade-in on a repeat tap
    shareToast.textContent = message;
    shareToast.classList.add("is-visible");
    clearTimeout(shareToastTimer);
    shareToastTimer = setTimeout(function () {
      shareToast.classList.remove("is-visible");
    }, 2200);
  }

  /* ── Share: one rule for every share button on the page ──────

     A phone and a desktop need opposite answers here.

     On a touch device the system share sheet is the best share UI there is:
     it lists the apps actually installed — WhatsApp, Instagram, Messages —
     and nothing a web page builds can match it. On a desktop the same call
     opens the Windows or macOS share flyout, which offers Nearby Sharing and
     Mail and rarely anything a shopper wants, so the Share button looked
     broken: "it doesn't show all the ways to share". Desktop gets a menu of
     every network that publishes a share link instead.

     Decided by input, not by window width: a narrow desktop window is still
     a desktop, and a tablet held in landscape is still a touch device. */
  function useNativeSheet() {
    return !!navigator.share && !!window.matchMedia &&
      window.matchMedia("(hover: none) and (pointer: coarse)").matches;
  }

  /* The storefront's own URL, never window.location.href on its own — under
     `shopify theme dev` that is 127.0.0.1 and on a preview it is the
     *.myshopify.com host, neither of which a recipient can open (see the
     data-share-origin note in sections/product.liquid). Read at click time,
     so the ?variant= the shopper has picked since the page loaded travels
     with the link. */
  function shareUrl() {
    var el = byId("pdpShare");
    var origin = el && el.dataset.shareOrigin;
    return origin
      ? origin.replace(/\/+$/, "") + window.location.pathname + window.location.search
      : window.location.href;
  }

  var SHARE_ICONS = {
    copy: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M10 13a5 5 0 0 0 7.5.5l3-3a5 5 0 0 0-7-7l-1.7 1.7"/><path d="M14 11a5 5 0 0 0-7.5-.5l-3 3a5 5 0 0 0 7 7l1.7-1.7"/></svg>',
    x: '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M17.8 3h3.1l-6.8 7.8L22 21h-6.2l-4.9-6.4L5.3 21H2.2l7.3-8.3L1.9 3h6.4l4.4 5.8L17.8 3zm-1.1 16.2h1.7L7.4 4.7H5.6l11.1 14.5z"/></svg>',
    telegram: '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M21.9 4.6 18.7 19.7c-.2 1.1-.9 1.3-1.8.8l-4.9-3.6-2.4 2.3c-.3.3-.5.5-1 .5l.3-5 9.1-8.2c.4-.4-.1-.6-.6-.2L6.2 13.4l-4.8-1.5c-1-.3-1.1-1 .2-1.5L20.5 3.2c.9-.3 1.6.2 1.4 1.4z"/></svg>',
    pinterest: '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 2a10 10 0 0 0-3.6 19.3c-.1-.8-.2-2 0-2.9l1.2-5s-.3-.6-.3-1.5c0-1.4.8-2.4 1.8-2.4.9 0 1.3.7 1.3 1.5 0 .9-.6 2.2-.9 3.4-.2 1 .5 1.9 1.5 1.9 1.8 0 3.2-1.9 3.2-4.7 0-2.5-1.8-4.2-4.3-4.2-2.9 0-4.6 2.2-4.6 4.4 0 .9.3 1.8.8 2.3l.1.4-.3 1.2c0 .2-.2.3-.4.2-1.3-.6-2.1-2.5-2.1-4 0-3.3 2.4-6.3 6.9-6.3 3.6 0 6.4 2.6 6.4 6 0 3.6-2.3 6.5-5.4 6.5-1.1 0-2.1-.6-2.4-1.2l-.7 2.5c-.2 1-.9 2.2-1.4 2.9A10 10 0 1 0 12 2z"/></svg>',
    linkedin: '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M20.4 20.5h-3.6v-5.6c0-1.3 0-3-1.8-3s-2.1 1.4-2.1 2.9v5.7H9.4V9h3.4v1.6c.5-.9 1.6-1.8 3.4-1.8 3.6 0 4.3 2.4 4.3 5.5v6.2zM5.3 7.4a2.1 2.1 0 1 1 0-4.2 2.1 2.1 0 0 1 0 4.2zM7.1 20.5H3.6V9h3.5v11.5z"/></svg>'
  };

  // The WhatsApp, Facebook and email glyphs already exist in the share row
  // (sections/product.liquid). Borrowed from there rather than drawn twice,
  // so the menu and the row can never show two different WhatsApp logos.
  function rowIcon(id) {
    var svg = byId(id) && byId(id).querySelector("svg");
    return svg ? svg.outerHTML : "";
  }

  function shareTargets() {
    var url = shareUrl();
    var title = document.title;
    var enc = encodeURIComponent;
    // Pinterest pins an image, not a page — it refuses a share without one.
    var ogImage = document.querySelector('meta[property="og:image"]');
    var image = (ogImage && ogImage.content) || (mainImg && mainImg.currentSrc) || "";
    return [
      { label: T.share_whatsapp || "WhatsApp", icon: rowIcon("pdpShareWhatsapp"),
        href: "https://wa.me/?text=" + enc(title + " " + url) },
      { label: T.share_facebook || "Facebook", icon: rowIcon("pdpShareFacebook"),
        href: "https://www.facebook.com/sharer/sharer.php?u=" + enc(url) },
      { label: T.share_x || "X", icon: SHARE_ICONS.x,
        href: "https://twitter.com/intent/tweet?text=" + enc(title) + "&url=" + enc(url) },
      { label: T.share_telegram || "Telegram", icon: SHARE_ICONS.telegram,
        href: "https://t.me/share/url?url=" + enc(url) + "&text=" + enc(title) },
      { label: T.share_pinterest || "Pinterest", icon: SHARE_ICONS.pinterest,
        href: "https://pinterest.com/pin/create/button/?url=" + enc(url) +
          "&media=" + enc(image) + "&description=" + enc(title) },
      { label: T.share_linkedin || "LinkedIn", icon: SHARE_ICONS.linkedin,
        href: "https://www.linkedin.com/sharing/share-offsite/?url=" + enc(url) },
      // mailto stays in this tab: a new blank tab that immediately hands off
      // to the mail app is just a stray tab left behind.
      { label: T.share_email || "Email", icon: rowIcon("pdpShareEmail"),
        href: "mailto:?subject=" + enc(title) + "&body=" + enc(title + "\n\n" + url), sameTab: true }
    ];
  }

  var shareMenu = null;
  var shareMenuFor = null;

  function shareMenuItems() {
    return shareMenu ? shareMenu.querySelectorAll("[role=menuitem]") : [];
  }

  /* Rebuilt on every open rather than once: the hrefs carry the selected
     variant, which changes as the shopper picks options. Labels go in as
     textContent, never markup — they come from locale files, and the icons
     are the only HTML here, all of it ours. */
  function buildShareMenu() {
    if (!shareMenu) {
      shareMenu = document.createElement("div");
      shareMenu.className = "pdp-share-menu";
      shareMenu.id = "pdpShareMenu";
      shareMenu.setAttribute("role", "menu");
      shareMenu.hidden = true;
      document.body.appendChild(shareMenu);
      shareMenu.addEventListener("keydown", onShareMenuKey);
      // Leaving the menu by Tab closes it, the same as clicking away.
      shareMenu.addEventListener("focusout", function (e) {
        if (!shareMenu.hidden && !shareMenu.contains(e.relatedTarget)) closeShareMenu(false);
      });
    }
    shareMenu.setAttribute("aria-label", T.share_menu || "Share this product");
    shareMenu.innerHTML = "";

    var copy = document.createElement("button");
    copy.type = "button";
    copy.className = "pdp-share-menu-item";
    copy.setAttribute("role", "menuitem");
    copy.innerHTML = '<span class="pdp-share-menu-icon" aria-hidden="true">' + SHARE_ICONS.copy + "</span>";
    copy.appendChild(document.createTextNode(T.copy_link || "Copy link"));
    copy.addEventListener("click", function () {
      copyText(shareUrl(), function () {
        showShareToast(T.link_copied || "Link copied");
      }, function () {
        showShareToast(T.copy_failed || "Couldn’t copy the link. Please try again.");
      });
      closeShareMenu(true);
    });
    shareMenu.appendChild(copy);

    shareTargets().forEach(function (t) {
      var a = document.createElement("a");
      a.className = "pdp-share-menu-item";
      a.setAttribute("role", "menuitem");
      a.href = t.href;
      if (!t.sameTab) {
        a.target = "_blank";
        a.rel = "noopener noreferrer";
      }
      a.innerHTML = '<span class="pdp-share-menu-icon" aria-hidden="true">' + t.icon + "</span>";
      a.appendChild(document.createTextNode(t.label));
      a.addEventListener("click", function () { closeShareMenu(false); });
      shareMenu.appendChild(a);
    });
  }

  /* Fixed to the viewport, beside whichever share button opened it — the
     same menu serves the share row, the sticky top bar and the gallery
     overlay. Opens below the button and right-aligned to it, flips above
     when there is no room below, and is held inside the window
     horizontally so a button near an edge never pushes it off screen. */
  function placeShareMenu() {
    if (!shareMenu || !shareMenuFor) return;
    var gap = 8;
    var r = shareMenuFor.getBoundingClientRect();
    var w = shareMenu.offsetWidth;
    var h = shareMenu.offsetHeight;
    var left = Math.min(Math.max(gap, r.right - w), window.innerWidth - w - gap);
    var top = r.bottom + gap;
    if (top + h > window.innerHeight - gap && r.top - gap - h >= gap) top = r.top - gap - h;
    shareMenu.style.left = Math.round(left) + "px";
    shareMenu.style.top = Math.round(Math.max(gap, top)) + "px";
  }

  function openShareMenu(btn) {
    buildShareMenu();
    if (shareMenuFor && shareMenuFor !== btn) shareMenuFor.setAttribute("aria-expanded", "false");
    shareMenuFor = btn;
    btn.setAttribute("aria-haspopup", "menu");
    btn.setAttribute("aria-controls", "pdpShareMenu");
    btn.setAttribute("aria-expanded", "true");
    shareMenu.hidden = false;
    placeShareMenu();
    var first = shareMenuItems()[0];
    if (first) first.focus();
  }

  function closeShareMenu(returnFocus) {
    if (!shareMenu || shareMenu.hidden) return;
    shareMenu.hidden = true;
    if (shareMenuFor) {
      shareMenuFor.setAttribute("aria-expanded", "false");
      if (returnFocus) shareMenuFor.focus();
    }
  }

  function onShareMenuKey(e) {
    var items = Array.prototype.slice.call(shareMenuItems());
    var i = items.indexOf(document.activeElement);
    if (e.key === "Escape") { e.preventDefault(); closeShareMenu(true); }
    else if (e.key === "ArrowDown") { e.preventDefault(); items[(i + 1) % items.length].focus(); }
    else if (e.key === "ArrowUp") { e.preventDefault(); items[(i - 1 + items.length) % items.length].focus(); }
    else if (e.key === "Home") { e.preventDefault(); items[0].focus(); }
    else if (e.key === "End") { e.preventDefault(); items[items.length - 1].focus(); }
  }

  document.addEventListener("click", function (e) {
    if (!shareMenu || shareMenu.hidden) return;
    if (shareMenu.contains(e.target) || (shareMenuFor && shareMenuFor.contains(e.target))) return;
    closeShareMenu(false);
  });
  // A fixed menu would stay put while the button it belongs to scrolled away,
  // so it closes instead. Safe here, unlike the country picker: every item is
  // inside the window, so moving focus between them never scrolls the page.
  window.addEventListener("scroll", function () { closeShareMenu(false); }, { passive: true });
  window.addEventListener("resize", function () { closeShareMenu(false); });

  /* ── Share buttons: sticky top bar + mobile gallery overlay ──
     Both use the same [data-pdp-share] contract — native share sheet
     first, otherwise copy the link and flash a checkmark (same
     feedback pattern as the page's own Instagram share button below,
     since this theme has no toast system). */
  root.querySelectorAll("[data-pdp-share]").forEach(function (shareBtn) {
    shareBtn.addEventListener("click", function () {
      if (useNativeSheet()) {
        navigator.share({ title: document.title, url: shareUrl() }).catch(function () {});
        return;
      }
      if (shareMenu && shareMenuFor === shareBtn && !shareMenu.hidden) {
        closeShareMenu(true);
      } else {
        openShareMenu(shareBtn);
      }
    });
  });

  /* ── Delegated clicks ─────────────────────────────────────── */
  function onPdpClick(e) {
    /* "Complete the Look" pill on the main image — jumps to the list below. */
    if (e.target.closest("#pdpCtlJump")) {
      var ctl = document.querySelector(".pdp-complete");
      if (ctl) ctl.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }

    /* Wishlist — the buy-row button and any related-card button. */
    var wish = e.target.closest("[data-wish-btn]");
    if (wish) { toggleWishlist(wish); return; }

    /* Zoom → lightbox (skipped on hover-capable devices; hovering already zooms). */
    if (e.target.closest("[data-zoom]")) { if (!hoverZoomCapable) openLightbox(); return; }

    /* Lightbox controls */
    if (e.target.closest("[data-lbclose]")) { closeLightbox(); return; }
    if (e.target.closest("[data-lbprev]")) { lbSet(lbIndex - 1); return; }
    if (e.target.closest("[data-lbnext]")) { lbSet(lbIndex + 1); return; }
    var lbThumb = e.target.closest("[data-lbthumb]");
    if (lbThumb && lightbox) {
      var thumbs = Array.prototype.slice.call(lightbox.querySelectorAll("[data-lbthumb]"));
      lbSet(thumbs.indexOf(lbThumb));
      return;
    }
    if (e.target === lightbox) { closeLightbox(); return; }

    /* Review modal open / close / stars / submit */
    if (e.target.closest("[data-review-open]")) { openReview(); return; }
    if (e.target.closest("[data-review-close]")) { closeReview(); return; }
    if (e.target === reviewModal) { closeReview(); return; }
    var starBtn = e.target.closest("[data-star]");
    if (starBtn) { reviewState.formRating = Number(starBtn.dataset.star); paintFormStars(); return; }
    if (e.target.closest("#pdpRvSubmit")) { submitReview(); return; }

    /* Mozoon loyalty side panel */
    if (e.target.closest("[data-mozoon-open]")) { openMozoon(); return; }
    if (e.target.closest("[data-mozoon-close]")) { closeMozoon(); return; }
    if (e.target === mzScrim) { closeMozoon(); return; }
    if (e.target.closest("[data-mz-info]")) { e.preventDefault(); return; }

    /* Size guide — the overlay for products with a Size option. Products
       without that option don't render this button at all (see show_size_guide
       in product.liquid), so there's nothing to no-op here. */
    if (e.target.closest("[data-size-guide-open]")) { openSizeGuide(); return; }
    if (e.target.closest("[data-size-guide-close]")) { closeSizeGuide(); return; }
    if (e.target === sgScrim) { closeSizeGuide(); return; }
    var sgChartBtn = e.target.closest("[data-sg-chart-btn]");
    if (sgChartBtn) { setSgChart(sgChartBtn.dataset.sgChartBtn); return; }
    if (e.target.closest("[data-sg-unit-toggle]")) { setSgUnit(sgState.unit === "cm" ? "in" : "cm"); return; }
    /* Measurement Guide link → measuring-guide sub-view; back arrow returns */
    if (e.target.closest("[data-sg-measure-guide]")) {
      e.preventDefault();
      if (sgOverlay) { sgOverlay.classList.add("is-guide"); sgOverlay.scrollTop = 0; }
      return;
    }
    if (e.target.closest("[data-sg-guide-close]")) {
      if (sgOverlay) { sgOverlay.classList.remove("is-guide"); sgOverlay.scrollTop = 0; }
      return;
    }
    /* Any option value — swatch or chip, desktop block or mobile sheet.
       One handler for all of them: the button carries its own option
       position, and every copy at that position (there are two of each,
       desktop + sheet) is re-marked together, which is what keeps the
       two views in step without either knowing about the other. */
    var optBtn = e.target.closest("[data-opt-pos]");
    if (optBtn && !optBtn.disabled) {
      var pos = parseInt(optBtn.dataset.optPos, 10);
      var value = optBtn.dataset.optValue;
      state.selected[pos] = value;

      // Whatever is picked at the other position(s) may not combine with this
      // value into a variant worth landing on. Two distinct failures, and only
      // the first used to be handled:
      //   - no such variant exists at all (a colour that only comes in one
      //     size), which this guard was written for;
      //   - the variant exists but is SOLD OUT, which it was not. Switching
      //     colour kept the previous size, so picking a colour whose stock
      //     sits in a different size landed on Sold Out at full price with
      //     the in-stock size one click away and nothing pointing at it.
      //     Testing whether a variant EXISTS rather than whether it can be
      //     BOUGHT is what let that through.
      // Re-seed the other positions from whichever variant this value really
      // resolves to (available + cheapest). The clicked value itself is never
      // moved: the shopper asked for it, so a colour that is genuinely sold
      // out in every size still lands on it and says so.
      var current = findVariant(state.selected);
      if (!current || !current.available) {
        var seedSelection = {};
        seedSelection[pos] = value;
        var seed = findVariant(seedSelection);
        // Adopt a buyable combination; or any real one when the current
        // picks resolve to nothing at all, so the picker is never left empty.
        if (seed && (seed.available || !current)) {
          optionPositions.forEach(function (p) {
            if (p !== pos) state.selected[p] = (seed.options || [])[p - 1];
          });
        }
      }

      optionPositions.forEach(function (p) {
        var want = state.selected[p];
        root.querySelectorAll('[data-opt-pos="' + p + '"]').forEach(function (el) {
          var active = el.dataset.optValue === want;
          el.classList.toggle("is-active", active);
          if (el.hasAttribute("aria-pressed")) {
            el.setAttribute("aria-pressed", active ? "true" : "false");
          }
        });
      });
      recomputeOptionAvailability();

      if (sizeHintEl) sizeHintEl.hidden = true;
      if (vsSizeHintEl) vsSizeHintEl.hidden = true;
      syncOptBar();
      var picked = findVariant(state.selected);
      applyVariant(picked);
      applyVariantGallery(picked);
      return;
    }

    /* Accordion header → expand/collapse */
    var accHead = e.target.closest(".pdp-acc-head");
    if (accHead) {
      var acc = accHead.parentNode;
      var open = acc.classList.toggle("is-open");
      accHead.setAttribute("aria-expanded", open);
      return;
    }

    /* Share on Instagram — opens the system share sheet, which lists Instagram
       alongside the other installed apps.

       Its neighbours are plain <a>s because each of those platforms publishes a
       share endpoint: facebook.com/sharer/sharer.php?u=, wa.me/?text=, mailto:.
       Instagram publishes none, and cannot — a link is not a thing you can post
       to Instagram at all; links live in a bio or a story sticker, both added
       inside the app. So there is no URL to point an <a> at, and no dialog to
       open. The share sheet is the one real route to the app from a web page.

       Browsers without navigator.share (Firefox on desktop) never render this
       button — see hideInstagramShareWithoutShareSheet below. A dead control is
       worse than one less icon, and the clipboard copy that used to stand in
       here just flashed a tick and left the shopper guessing. */
    if (e.target.closest("[data-share-instagram]")) {
      e.preventDefault();
      if (navigator.share) {
        var shareRoot = byId("pdpShare");
        navigator.share({
          title: document.title,
          url: (shareRoot && shareRoot.dataset.shareUrl) || window.location.href
        }).catch(function () {}); /* cancelled: nothing happened, so say nothing */
      }
      return;
    }

    /* Variant sheet — either .pdp-optbar cell opens it */
    if (e.target.closest("[data-varsheet-open]")) { openVarSheet(); return; }
    if (e.target.closest("[data-varsheet-close]")) { closeVarSheet(); return; }
    if (e.target === varScrim) { closeVarSheet(); return; }

    /* Add to bag from inside the sheet — same size gate as the other
       two entry points, then close so the confirmation toast is
       visible rather than sitting behind the sheet. */
    if (e.target.closest("#pdpVsAdd")) {
      var vsMissing = missingOption();
      if (vsMissing) {
        var vsLabel = (optionNames[vsMissing] || "option").toLowerCase();
        if (vsSizeHintEl) {
          vsSizeHintEl.textContent = selectOptionText(vsLabel);
          vsSizeHintEl.hidden = false;
        }
        return;
      }
      addToCart(variantIdInput.value).then(function (ok) {
        if (ok) closeVarSheet();
      });
      return;
    }

    /* Sticky add-to-cart bar */
    if (e.target.closest("#pdpSbAdd")) {
      var missing = missingOption();
      if (missing) { flagMissingOption(missing); return; }
      addToCart(variantIdInput.value).then(function (ok) {
        if (ok) flashButton(sbAddBtn, T.added_check || "Added to bag ✓",
          T.add_to_bag || "Add to bag", null, sbAddTextEl);
      });
      return;
    }

    /* "Complete the Look" quick shop — a specific variant, no size gate */
    var quickAdd = e.target.closest("[data-quick-add-variant]");
    if (quickAdd && !quickAdd.disabled) {
      addToCart(quickAdd.dataset.quickAddVariant).then(function (ok) {
        if (ok) flashButton(quickAdd, T.added_short || "Added ✓",
          T.quick_shop || "Quick Shop");
      });
      return;
    }

  }

  root.addEventListener("click", onPdpClick);

  // Quick View links here instead of duplicating the sizeable chart and its
  // interaction code in every listing drawer. Opening from the URL also
  // preserves a useful no-JS destination: the shopper still reaches the PDP.
  if (window.location.hash === "#size-guide") window.setTimeout(openSizeGuide, 0);

  /* Reviews are their own section now (sections/pdp-reviews.liquid), so
     the composer's open/close/star/submit targets sit outside #pdp and
     never reach the listener above — bind the same handler to the
     reviews section's own root as well. Nothing else needs this: "You
     may also like" is entirely assets/listing.js's (its cards are real
     .plp-card markup, and its delegated handlers are document-level). */
  var reviewsSection = byId("pdpReviewsSection");
  if (reviewsSection) reviewsSection.addEventListener("click", onPdpClick);

  /* Main "Add to cart" — intercept the form submit for the Ajax cart API */
  var pdpForm = byId("pdpForm");
  if (pdpForm) {
    pdpForm.addEventListener("submit", function (e) {
      e.preventDefault();
      var missing = missingOption();
      if (missing) { flagMissingOption(missing); return; }
      addToCart(variantIdInput.value).then(function (ok) {
        if (ok) flashButton(addBagBtn, T.added_check || "Added to bag ✓",
          T.add_to_bag || "Add to bag", null, addBagTextEl);
      });
    });
  }

  /* Keyboard: Esc closes overlays; arrows page the lightbox. */
  document.addEventListener("keydown", function (e) {
    if (e.key === "Escape") {
      if (lightbox && !lightbox.hidden) closeLightbox();
      if (reviewModal && !reviewModal.hidden) closeReview();
      if (mzDrawer && !mzDrawer.hidden) closeMozoon();
      if (varSheet && !varSheet.hidden) closeVarSheet();
      if (sgOverlay && !sgOverlay.hidden) {
        // From the measuring-guide sub-view, Esc steps back to the chart first.
        if (sgOverlay.classList.contains("is-guide")) sgOverlay.classList.remove("is-guide");
        else closeSizeGuide();
      }
      return;
    }
    if (lightbox && !lightbox.hidden && gallerySrcs.length > 1) {
      if (e.key === "ArrowLeft") lbSet(lbIndex - 1);
      else if (e.key === "ArrowRight") lbSet(lbIndex + 1);
    }
  });

  /* Details: most descriptions carry their own "SPECIFICATIONS" and "About
     Brand" parts, each introduced by a bold heading. Each becomes an
     accordion row of its own straight after Details, titled from the locale
     (the data-label-* attributes), instead of sitting inside Details.
     Headings are found wherever they sit - leading a paragraph, or mid-span
     inside older pasted markup (tables, spans) - and the description is cut
     at each one with a Range, so the wrappers are carried along and the text
     is only moved, never rewritten. A Details row left empty is removed. */
  function splitDescriptionSections() {
    var details = document.querySelector("[data-desc-acc]");
    var inner = details && details.querySelector(".pdp-acc-inner");
    if (!inner) return;
    var kinds = [
      { key: "specs", re: /^(specifications?|product specifications|key features|المواصفات|مواصفات المنتج)$/i },
      { key: "brand", re: /^(about( the)? brand|about the designer|brand story|حول العلامة التجارية|عن العلامة التجارية|نبذة عن العلامة التجارية)$/i }
    ];
    var heads = [];
    Array.prototype.forEach.call(inner.querySelectorAll("strong, b, h2, h3, h4, h5, h6"), function (el) {
      // Skip a bold run nested in another heading already matched.
      for (var i = 0; i < heads.length; i++) if (heads[i].el.contains(el)) return;
      var text = el.textContent.replace(/[\s\u00a0]+/g, " ").replace(/[:：\s]+$/, "").trim();
      for (var k = 0; k < kinds.length; k++) {
        if (kinds[k].re.test(text)) { heads.push({ key: kinds[k].key, el: el }); return; }
      }
    });
    if (!heads.length) return;
    // Cut from the last heading backwards so earlier cuts keep their ends.
    var parts = [];
    for (var h = heads.length - 1; h >= 0; h--) {
      var range = document.createRange();
      range.setStartAfter(heads[h].el);
      range.setEnd(inner, inner.childNodes.length);
      var frag = range.extractContents();
      // A paragraph led by the heading leaves an empty shell behind; drop the
      // heading (and that shell) from Details.
      var shell = heads[h].el.parentNode;
      heads[h].el.remove();
      while (shell && shell !== inner && !shell.textContent.trim() && !shell.querySelector("img, iframe, video")) {
        var up = shell.parentNode; shell.remove(); shell = up;
      }
      parts.unshift({ key: heads[h].key, frag: frag });
    }
    function trimEdges(root) {
      // Line breaks and blank text left at either edge of a cut.
      ["firstChild", "lastChild"].forEach(function (edge) {
        var node;
        while ((node = root[edge])) {
          var media = node.nodeType === 1 && (/^(IMG|IFRAME|VIDEO)$/.test(node.nodeName) || node.querySelector("img, iframe, video"));
          var blank = node.nodeName === "BR" || (!node.textContent.trim() && !media);
          if (!blank) break;
          node.remove();
        }
        if (node && node.nodeType === 1) trimEdges(node);
      });
    }
    var chevron = details.querySelector(".pdp-acc-chev");
    var after = details;
    parts.forEach(function (part) {
      var bodyInner = document.createElement("div");
      bodyInner.className = "pdp-acc-inner";
      bodyInner.appendChild(part.frag);
      trimEdges(bodyInner);
      if (!bodyInner.textContent.trim()) return;
      var row = document.createElement("div");
      row.className = "pdp-acc";
      var head = document.createElement("button");
      head.type = "button";
      head.className = "pdp-acc-head";
      head.setAttribute("aria-expanded", "false");
      var label = document.createElement("span");
      label.textContent = details.getAttribute(part.key === "specs" ? "data-label-specs" : "data-label-brand");
      head.appendChild(label);
      if (chevron) head.appendChild(chevron.cloneNode(true));
      var body = document.createElement("div");
      body.className = "pdp-acc-body";
      body.appendChild(bodyInner);
      row.appendChild(head);
      row.appendChild(body);
      after.parentNode.insertBefore(row, after.nextSibling);
      after = row;
    });
    trimEdges(inner);
    if (!inner.textContent.trim() && !inner.querySelector("img, iframe, video")) details.remove();
  }
  splitDescriptionSections();

  hydrateWishlist();
  // Fills the collapsed bar's colour dot, which needs the thumbnail URLs
  // renderSwatchThumbnails() resolves rather than anything Liquid can print.
  syncOptBar();
  // applyVariant() only runs on a change, so the note would stay hidden
  // until the first tap — surface it for the variant already selected.
  applyLowStock(findVariant(state.selected));
})();
